package com.havn.app.features.analytics.presentation

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.slideInHorizontally
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnScreenFrame
import com.havn.app.core.designsystem.HavnSpacing
import com.havn.app.core.designsystem.HavnStatCard
import com.havn.app.core.designsystem.havnIsWebShell
import com.havn.app.core.export.HavnXlsxSheet
import com.havn.app.core.export.HavnXlsxMimeType
import com.havn.app.core.export.buildHavnXlsx
import com.havn.app.core.localization.HavnLanguage
import com.havn.app.core.localization.HavnStrings
import com.havn.app.core.localization.LocalHavnLanguage
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.core.media.HavnPickedFile
import com.havn.app.core.media.havnPropertyPhotoPreviewSource
import com.havn.app.core.presentation.havnFriendlyMessage
import com.havn.app.features.analytics.data.loadStoredAnalyticsAiInsights
import com.havn.app.features.analytics.data.storeAnalyticsAiInsights
import com.havn.app.features.attachments.domain.HavnAttachment
import com.havn.app.features.attachments.domain.HavnAttachmentRecipient
import com.havn.app.features.attachments.presentation.AttachmentContactPickerMode
import com.havn.app.features.attachments.presentation.AttachmentRecipientFilter
import com.havn.app.features.attachments.presentation.AttachmentRecipientPickerScreen
import com.havn.app.features.analytics.domain.AnalyticsAiAgentPerformance
import com.havn.app.features.analytics.domain.AnalyticsAiConversionFunnel
import com.havn.app.features.analytics.domain.AnalyticsAiInput
import com.havn.app.features.analytics.domain.AnalyticsAiPropertyPerformance
import com.havn.app.features.analytics.domain.AnalyticsAiRecentTrends
import com.havn.app.features.analytics.domain.AnalyticsAiService
import com.havn.app.features.analytics.domain.AnalyticsAiSummaryMetrics
import com.havn.app.features.agency.domain.AgencyMember
import com.havn.app.features.agency.presentation.AgencyViewModel
import com.havn.app.features.followups.domain.FollowUp
import com.havn.app.features.followups.domain.FollowUpChannel
import com.havn.app.features.followups.presentation.FollowUpsViewModel
import com.havn.app.features.leads.domain.Lead
import com.havn.app.features.leads.presentation.LeadsViewModel
import com.havn.app.features.openhouses.domain.LeadTemperature
import com.havn.app.features.openhouses.domain.OpenHouse
import com.havn.app.features.openhouses.presentation.OpenHousesViewModel
import com.havn.app.features.properties.domain.Property
import com.havn.app.features.properties.presentation.PropertiesViewModel
import com.havn.app.ui.theme.LocalHavnThemeTokens
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlin.math.roundToInt

@Composable
fun AnalyticsScreen(
    propertiesViewModel: PropertiesViewModel,
    openHousesViewModel: OpenHousesViewModel,
    leadsViewModel: LeadsViewModel,
    followUpsViewModel: FollowUpsViewModel,
    agencyViewModel: AgencyViewModel,
    analyticsAiService: AnalyticsAiService,
    onBackToToday: (() -> Unit)? = null,
    agentName: String? = null,
    agencyName: String? = null,
    shareRecipients: List<HavnAttachmentRecipient> = emptyList(),
    linkedShareRecipients: List<HavnAttachmentRecipient> = emptyList(),
    recentShareRecipients: List<HavnAttachmentRecipient> = emptyList(),
    isLoadingShareRecipients: Boolean = false,
    isLoadingMoreShareRecipients: Boolean = false,
    hasMoreShareRecipients: Boolean = false,
    isUploadingPdf: Boolean = false,
    onUploadAnalyticsPdf: (HavnPickedFile, (HavnAttachment) -> Unit, (String) -> Unit) -> Unit = { _, _, _ -> },
    onOpenAnalyticsPdf: (HavnAttachment) -> Unit = {},
    onSendAnalyticsEmail: (List<HavnAttachmentRecipient>, HavnAttachment) -> Unit = { _, _ -> },
    onSendAnalyticsSms: (List<HavnAttachmentRecipient>, HavnAttachment) -> Unit = { _, _ -> },
    onOpenShareRecipients: () -> Unit = {},
    onShareRecipientQueryChange: (String) -> Unit = {},
    onLoadMoreShareRecipients: () -> Unit = {},
    onRecordShareRecipients: (List<HavnAttachmentRecipient>) -> Unit = {},
    scrollToTopSignal: Int = 0
) {
    val propertiesState by propertiesViewModel.uiState.collectAsState()
    val openHousesState by openHousesViewModel.uiState.collectAsState()
    val leadsState by leadsViewModel.uiState.collectAsState()
    val followUpsState by followUpsViewModel.uiState.collectAsState()
    val agencyState by agencyViewModel.uiState.collectAsState()
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val language = LocalHavnLanguage.current
    var datePreset by remember { mutableStateOf(AnalyticsDatePreset.AllTime) }
    var dateFrom by remember { mutableStateOf<String?>(null) }
    var dateTo by remember { mutableStateOf<String?>(null) }
    var datePickerTarget by remember { mutableStateOf<AnalyticsDateTarget?>(null) }
    var agentFilterId by remember { mutableStateOf<String?>(null) }
    var aiInsightsJson by remember { mutableStateOf("") }
    var aiInsightsLoading by remember { mutableStateOf(false) }
    var aiInsightsError by remember { mutableStateOf<String?>(null) }
    var generatedPdf by remember { mutableStateOf<GeneratedAnalyticsExport?>(null) }
    var uploadedPdf by remember { mutableStateOf<HavnAttachment?>(null) }
    var pdfError by remember { mutableStateOf<String?>(null) }
    var contactPickerMode by remember { mutableStateOf<AttachmentContactPickerMode?>(null) }
    var recipientQuery by remember { mutableStateOf("") }
    var recipientFilter by remember { mutableStateOf(AttachmentRecipientFilter.All) }
    var selectedRecipientIds by remember(contactPickerMode) { mutableStateOf(emptySet<String>()) }
    val scope = rememberCoroutineScope()

    val agentOptions = remember(agencyState.members, propertiesState.currentAgentId) {
        buildAgentOptions(agencyState.members, propertiesState.currentAgentId, strings)
    }
    val activityDateBounds = remember(openHousesState.openHouses, leadsState.leads, followUpsState.followUps) {
        analyticsActivityDateBounds(
            openHouses = openHousesState.openHouses,
            leads = leadsState.leads,
            followUps = followUpsState.followUps
        )
    }
    val filteredData = remember(
        dateFrom,
        dateTo,
        agentFilterId,
        propertiesState.properties,
        openHousesState.openHouses,
        leadsState.leads,
        followUpsState.followUps
    ) {
        filterAnalyticsData(
            dateFrom = dateFrom,
            dateTo = dateTo,
            agentId = agentFilterId,
            properties = propertiesState.properties,
            openHouses = openHousesState.openHouses,
            leads = leadsState.leads,
            followUps = followUpsState.followUps
        )
    }
    val selectedDateRangeLabel = remember(datePreset, dateFrom, dateTo, strings) {
        analyticsSelectedDateRangeLabel(datePreset, dateFrom, dateTo, strings)
    }

    val metrics = buildAnalyticsMetrics(
        properties = filteredData.properties,
        openHouses = filteredData.openHouses,
        leads = filteredData.leads,
        followUps = filteredData.followUps,
        members = agencyState.members,
        dateFrom = dateFrom,
        dateTo = dateTo,
        dateRangeLabel = selectedDateRangeLabel
    )
    val analyticsAiCacheKey = remember(
        agencyState.selectedAgencyId,
        agencyState.workspaceName,
        language,
        datePreset,
        dateFrom,
        dateTo,
        agentFilterId,
        metrics
    ) {
        buildAnalyticsAiCacheKey(
            agencyId = agencyState.selectedAgencyId,
            agencyName = agencyState.workspaceName,
            language = language,
            datePreset = datePreset,
            dateFrom = dateFrom,
            dateTo = dateTo,
            agentFilterId = agentFilterId,
            metrics = metrics
        )
    }
    LaunchedEffect(analyticsAiCacheKey) {
        if (!aiInsightsLoading) {
            aiInsightsJson = loadStoredAnalyticsAiInsights(analyticsAiCacheKey).orEmpty()
            aiInsightsError = null
        }
    }
    contactPickerMode?.let { mode ->
        Dialog(
            onDismissRequest = { contactPickerMode = null },
            properties = DialogProperties(usePlatformDefaultWidth = false)
        ) {
            Surface(color = LocalHavnThemeTokens.current.background, modifier = Modifier.fillMaxSize()) {
                Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
                    AnimatedVisibility(
                        visible = true,
                        enter = slideInHorizontally(animationSpec = tween(260), initialOffsetX = { it }) + fadeIn(animationSpec = tween(220))
                    ) {
                        AttachmentRecipientPickerScreen(
                            mode = mode,
                            query = recipientQuery,
                            selectedFilter = recipientFilter,
                            selectedRecipientIds = selectedRecipientIds,
                            allRecipients = shareRecipients,
                            linkedRecipients = linkedShareRecipients,
                            recentRecipients = recentShareRecipients,
                            isLoading = isLoadingShareRecipients,
                            isLoadingMore = isLoadingMoreShareRecipients,
                            hasMoreRecipients = hasMoreShareRecipients,
                            onQueryChange = {
                                recipientQuery = it
                                onShareRecipientQueryChange(it)
                            },
                            onFilterChange = { recipientFilter = it },
                            onBack = { contactPickerMode = null },
                            onToggleRecipient = { id -> selectedRecipientIds = selectedRecipientIds.toggleAnalyticsRecipient(id) },
                            onLoadMoreRecipients = onLoadMoreShareRecipients,
                            onSend = { recipients ->
                                contactPickerMode = null
                                val pdf = generatedPdf
                                val attachment = uploadedPdf
                                pdfError = when {
                                    pdf == null -> strings.pdfGenerateBeforeSending
                                    attachment == null -> strings.pdfUploadBeforeSharing
                                    mode == AttachmentContactPickerMode.Email -> {
                                        onSendAnalyticsEmail(recipients, attachment)
                                        null
                                    }
                                    mode == AttachmentContactPickerMode.Sms -> {
                                        onSendAnalyticsSms(recipients, attachment)
                                        null
                                    }
                                    else -> strings.pdfUploadBeforeSharing
                                }
                                if (pdfError == null) onRecordShareRecipients(recipients)
                            }
                        )
                    }
                }
            }
        }
    }

    HavnScreenFrame(
        eyebrow = "",
        title = strings.analyticsOverview,
        subtitle = "",
        backLabel = onBackToToday?.let { strings.navToday },
        onBack = onBackToToday,
        isRefreshing = propertiesState.isLoading || openHousesState.isLoading || leadsState.isLoading || followUpsState.isLoading,
        onRefresh = {
            propertiesViewModel.refresh()
            openHousesViewModel.refresh()
            leadsViewModel.refresh()
            followUpsViewModel.refresh()
            agencyViewModel.refresh()
        },
        scrollToTopSignal = scrollToTopSignal
    ) {
        BoxWithConstraints(
            Modifier
                .fillMaxSize()
                .background(Brush.verticalGradient(listOf(theme.background, theme.gradientStart, theme.background)))
        ) {
            val wide = havnIsWebShell && maxWidth >= 980.dp
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(14.dp)
            ) {
                AnalyticsToolbar(
                    datePreset = datePreset,
                    dateFrom = dateFrom,
                    dateTo = dateTo,
                    agentLabel = agentOptions.firstOrNull { it.agentId == agentFilterId }?.label ?: strings.allAgents,
                    agentOptions = agentOptions,
                    wide = wide,
                    onPresetSelected = { preset ->
                        datePreset = preset
                        val range = preset.rangeFor(activityDateBounds)
                        dateFrom = range.first
                        dateTo = range.second
                    },
                    onDateFromClick = { datePickerTarget = AnalyticsDateTarget.From },
                    onDateToClick = { datePickerTarget = AnalyticsDateTarget.To },
                    onAgentSelected = { agentFilterId = it }
                )

                AnalyticsKpiGrid(
                    metrics = metrics,
                    wide = wide
                )

                if (wide) {
                    Row(horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Top) {
                        FunnelCard(metrics, Modifier.weight(0.9f))
                        Column(modifier = Modifier.weight(1.35f), verticalArrangement = Arrangement.spacedBy(14.dp)) {
                            TrendCard(metrics.trendSeries, selectedDateRangeLabel, Modifier.fillMaxWidth())
                            InsightCard(
                                insight = metrics.insightText,
                                aiJson = aiInsightsJson,
                                isLoading = aiInsightsLoading,
                                error = aiInsightsError,
                                onGenerate = {
                                    aiInsightsLoading = true
                                    aiInsightsError = null
                                    scope.launch {
                                        analyticsAiService.generateInsights(
                                            metrics.toAiInput(
                                                language = language.aiLanguageInstruction(),
                                                agencyName = agencyState.workspaceName,
                                                filteredData = filteredData
                                            )
                                        ).onSuccess {
                                            aiInsightsJson = it
                                            storeAnalyticsAiInsights(analyticsAiCacheKey, it)
                                            aiInsightsLoading = false
                                        }.onFailure {
                                            aiInsightsError = it.havnFriendlyMessage("Unable to generate analytics AI insights.")
                                            aiInsightsLoading = false
                                        }
                                    }
                                },
                                modifier = Modifier.fillMaxWidth()
                            )
                        }
                    }
                    Row(horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Top) {
                        TopPropertiesCard(metrics.topProperties, Modifier.weight(1.15f))
                        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(14.dp)) {
                            LeadQualityCard(metrics.hotLeads, metrics.warmLeads, metrics.coldLeads, Modifier.fillMaxWidth())
                            FollowUpCard(metrics.followUpRate, metrics.completedFollowUps, metrics.totalLeads, Modifier.fillMaxWidth())
                        }
                    }
                    Row(horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Top) {
                        AgentPerformanceCard(metrics.agentRows, Modifier.weight(1.15f))
                        ChannelBreakdownCard(metrics.channelRows, Modifier.weight(1f))
                    }
                    AnalyticsPdfActions(
                        generatedPdf = generatedPdf,
                        uploadedPdf = uploadedPdf,
                        isUploadingPdf = isUploadingPdf,
                        error = pdfError,
                        onGenerate = {
                            val pdf = metrics.generateAnalyticsXlsx(
                                dateRange = selectedDateRangeLabel,
                                agentLabel = agentOptions.firstOrNull { it.agentId == agentFilterId }?.label ?: strings.allAgents,
                                agentName = agentName,
                                agencyName = agencyName ?: agencyState.workspaceName,
                                aiJson = aiInsightsJson,
                                strings = strings,
                                language = language
                            )
                            generatedPdf = pdf
                            uploadedPdf = null
                            pdfError = null
                            onUploadAnalyticsPdf(
                                HavnPickedFile(pdf.fileName, pdf.bytes, HavnXlsxMimeType),
                                { attachment ->
                                    uploadedPdf = attachment
                                    pdfError = null
                                },
                                { message -> pdfError = message }
                            )
                        },
                        onOpenPdf = { uploadedPdf?.let(onOpenAnalyticsPdf) },
                        onSendEmail = {
                            recipientQuery = ""
                            onOpenShareRecipients()
                            contactPickerMode = AttachmentContactPickerMode.Email
                        },
                        onSendSms = {
                            recipientQuery = ""
                            onOpenShareRecipients()
                            contactPickerMode = AttachmentContactPickerMode.Sms
                        }
                    )
                } else {
                    FunnelCard(metrics, Modifier.fillMaxWidth())
                    TrendCard(metrics.trendSeries, selectedDateRangeLabel, Modifier.fillMaxWidth())
                    InsightCard(
                        insight = metrics.insightText,
                        aiJson = aiInsightsJson,
                        isLoading = aiInsightsLoading,
                        error = aiInsightsError,
                        onGenerate = {
                            aiInsightsLoading = true
                            aiInsightsError = null
                            scope.launch {
                                analyticsAiService.generateInsights(
                                    metrics.toAiInput(
                                        language = language.aiLanguageInstruction(),
                                        agencyName = agencyState.workspaceName,
                                        filteredData = filteredData
                                    )
                                ).onSuccess {
                                    aiInsightsJson = it
                                    storeAnalyticsAiInsights(analyticsAiCacheKey, it)
                                    aiInsightsLoading = false
                                }.onFailure {
                                    aiInsightsError = it.havnFriendlyMessage("Unable to generate analytics AI insights.")
                                    aiInsightsLoading = false
                                }
                            }
                        },
                        modifier = Modifier.fillMaxWidth()
                    )
                    TopPropertiesCard(metrics.topProperties, Modifier.fillMaxWidth())
                    LeadQualityCard(metrics.hotLeads, metrics.warmLeads, metrics.coldLeads, Modifier.fillMaxWidth())
                    FollowUpCard(metrics.followUpRate, metrics.completedFollowUps, metrics.totalLeads, Modifier.fillMaxWidth())
                    AgentPerformanceCard(metrics.agentRows, Modifier.fillMaxWidth())
                    ChannelBreakdownCard(metrics.channelRows, Modifier.fillMaxWidth())
                    AnalyticsPdfActions(
                        generatedPdf = generatedPdf,
                        uploadedPdf = uploadedPdf,
                        isUploadingPdf = isUploadingPdf,
                        error = pdfError,
                        onGenerate = {
                            val pdf = metrics.generateAnalyticsXlsx(
                                dateRange = selectedDateRangeLabel,
                                agentLabel = agentOptions.firstOrNull { it.agentId == agentFilterId }?.label ?: strings.allAgents,
                                agentName = agentName,
                                agencyName = agencyName ?: agencyState.workspaceName,
                                aiJson = aiInsightsJson,
                                strings = strings,
                                language = language
                            )
                            generatedPdf = pdf
                            uploadedPdf = null
                            pdfError = null
                            onUploadAnalyticsPdf(
                                HavnPickedFile(pdf.fileName, pdf.bytes, HavnXlsxMimeType),
                                { attachment ->
                                    uploadedPdf = attachment
                                    pdfError = null
                                },
                                { message -> pdfError = message }
                            )
                        },
                        onOpenPdf = { uploadedPdf?.let(onOpenAnalyticsPdf) },
                        onSendEmail = {
                            recipientQuery = ""
                            onOpenShareRecipients()
                            contactPickerMode = AttachmentContactPickerMode.Email
                        },
                        onSendSms = {
                            recipientQuery = ""
                            onOpenShareRecipients()
                            contactPickerMode = AttachmentContactPickerMode.Sms
                        }
                    )
                }
            }
        }
    }

    datePickerTarget?.let { target ->
        AnalyticsDatePickerDialog(
            title = if (target == AnalyticsDateTarget.From) strings.analyticsDateFrom else strings.analyticsDateTo,
            initialValue = if (target == AnalyticsDateTarget.From) dateFrom else dateTo,
            fallbackValue = if (target == AnalyticsDateTarget.From) activityDateBounds.earliestDate else activityDateBounds.latestDate,
            onDismiss = { datePickerTarget = null },
            onConfirm = { value ->
                datePreset = AnalyticsDatePreset.Custom
                if (target == AnalyticsDateTarget.From) {
                    dateFrom = value
                    if (dateTo != null && value.isoDayNumberOrNull()?.let { fromDay ->
                            dateTo.isoDayNumberOrNull()?.let { toDay -> fromDay > toDay }
                        } == true
                    ) {
                        dateTo = value
                    }
                } else {
                    dateTo = value
                    if (dateFrom != null && value.isoDayNumberOrNull()?.let { toDay ->
                            dateFrom.isoDayNumberOrNull()?.let { fromDay -> toDay < fromDay }
                        } == true
                    ) {
                        dateFrom = value
                    }
                }
                datePickerTarget = null
            }
        )
    }
}

@Composable
private fun AnalyticsToolbar(
    datePreset: AnalyticsDatePreset,
    dateFrom: String?,
    dateTo: String?,
    agentLabel: String,
    agentOptions: List<AnalyticsAgentOption>,
    wide: Boolean,
    onPresetSelected: (AnalyticsDatePreset) -> Unit,
    onDateFromClick: () -> Unit,
    onDateToClick: () -> Unit,
    onAgentSelected: (String?) -> Unit
) {
    val strings = LocalHavnStrings.current
    if (wide) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(10.dp, Alignment.End),
            verticalAlignment = Alignment.CenterVertically
        ) {
            AnalyticsPresetDropdown(
                label = datePreset.label(strings),
                onPresetSelected = onPresetSelected,
                modifier = Modifier.weight(1f)
            )
            AnalyticsDateField(
                label = strings.analyticsDateFrom,
                value = dateFrom,
                onClick = onDateFromClick,
                modifier = Modifier.weight(1f)
            )
            AnalyticsDateField(
                label = strings.analyticsDateTo,
                value = dateTo,
                onClick = onDateToClick,
                modifier = Modifier.weight(1f)
            )
            AnalyticsAgentDropdown(
                label = agentLabel,
                options = agentOptions,
                onAgentSelected = onAgentSelected,
                modifier = Modifier.weight(1f)
            )
        }
    } else {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                AnalyticsPresetDropdown(
                    label = datePreset.label(strings),
                    onPresetSelected = onPresetSelected,
                    modifier = Modifier.weight(1f)
                )
                AnalyticsAgentDropdown(
                    label = agentLabel,
                    options = agentOptions,
                    onAgentSelected = onAgentSelected,
                    modifier = Modifier.weight(1f)
                )
            }
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                AnalyticsDateField(
                    label = strings.analyticsDateFrom,
                    value = dateFrom,
                    onClick = onDateFromClick,
                    modifier = Modifier.weight(1f)
                )
                AnalyticsDateField(
                    label = strings.analyticsDateTo,
                    value = dateTo,
                    onClick = onDateToClick,
                    modifier = Modifier.weight(1f)
                )
            }
        }
    }
}

@Composable
private fun AnalyticsPresetDropdown(
    label: String,
    onPresetSelected: (AnalyticsDatePreset) -> Unit,
    modifier: Modifier = Modifier
) {
    val strings = LocalHavnStrings.current
    var expanded by remember { mutableStateOf(false) }
    Box(modifier = modifier) {
        AnalyticsControlPill(
            label = strings.analyticsDatePreset,
            value = label,
            icon = HavnIconKey.Calendar,
            onClick = { expanded = true }
        )
        DropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
            AnalyticsDatePreset.entries.forEach { preset ->
                DropdownMenuItem(
                    text = { Text(preset.label(strings)) },
                    onClick = {
                        expanded = false
                        onPresetSelected(preset)
                    }
                )
            }
        }
    }
}

@Composable
private fun AnalyticsAgentDropdown(
    label: String,
    options: List<AnalyticsAgentOption>,
    onAgentSelected: (String?) -> Unit,
    modifier: Modifier = Modifier
) {
    val strings = LocalHavnStrings.current
    var expanded by remember { mutableStateOf(false) }
    Box(modifier = modifier) {
        AnalyticsControlPill(
            label = strings.selectedAgent,
            value = label,
            icon = HavnIconKey.User,
            onClick = { expanded = true }
        )
        DropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
            options.forEach { option ->
                DropdownMenuItem(
                    text = { Text(option.label) },
                    onClick = {
                        expanded = false
                        onAgentSelected(option.agentId)
                    }
                )
            }
        }
    }
}

@Composable
private fun AnalyticsDateField(
    label: String,
    value: String?,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    AnalyticsControlPill(
        label = label,
        value = value?.analyticsDateLabel() ?: LocalHavnStrings.current.analyticsAllTime,
        icon = HavnIconKey.Calendar,
        onClick = onClick,
        modifier = modifier
    )
}

@Composable
private fun AnalyticsControlPill(
    label: String,
    value: String,
    icon: HavnIconKey,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        color = theme.card,
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border),
        modifier = modifier.clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(min = 58.dp)
                .padding(horizontal = 14.dp, vertical = 9.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            HavnIcon(icon, contentDescription = label, tint = theme.primaryAccent, modifier = Modifier.size(18.dp))
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(label, color = theme.secondaryText, style = MaterialTheme.typography.labelSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(value, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, maxLines = 1, overflow = TextOverflow.Ellipsis)
            }
            HavnIcon(HavnIconKey.More, contentDescription = null, tint = theme.secondaryText, modifier = Modifier.size(16.dp))
        }
    }
}

@Composable
private fun AnalyticsKpiGrid(metrics: AnalyticsMetrics, wide: Boolean) {
    val strings = LocalHavnStrings.current
    if (wide) {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            KpiCard(strings.visitors, metrics.visitors.toString(), "${metrics.openHouseCount} ${strings.analyticsOpenHousesLowercase}", HavnIconKey.Users, Color(0xFF22D3EE), Modifier.weight(1f))
            KpiCard(strings.hotLeads, metrics.hotLeads.toString(), "${metrics.warmLeads} ${strings.analyticsWarmLeadsLowercase}", HavnIconKey.TrendUp, Color(0xFFF97316), Modifier.weight(1f))
            KpiCard(strings.commonInquiries, metrics.inquiries.toString(), "${metrics.generalInquiries} ${strings.generalInquiry} · ${metrics.propertyInquiries} ${strings.propertyInquiry}", HavnIconKey.Leads, Color(0xFF64748B), Modifier.weight(1f))
            KpiCard(strings.tourSignals, metrics.tourSignals.toString(), strings.analyticsFromNotesFollowUps, HavnIconKey.Calendar, Color(0xFF22C55E), Modifier.weight(1f))
            KpiCard(strings.analyticsHotConversion, "${metrics.conversion.oneDecimal()}%", strings.analyticsHotLeadsPerVisitors, HavnIconKey.Chart, Color(0xFFE11D48), Modifier.weight(1f))
            KpiCard(strings.analyticsAverageIntent, metrics.averageIntentLabel, strings.analyticsVisitorIntentScore, HavnIconKey.Today, Color(0xFF8B5CF6), Modifier.weight(1f))
        }
    } else {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                KpiCard(strings.visitors, metrics.visitors.toString(), "${metrics.openHouseCount} ${strings.analyticsOpenHousesLowercase}", HavnIconKey.Users, Color(0xFF22D3EE), Modifier.weight(1f))
                KpiCard(strings.hotLeads, metrics.hotLeads.toString(), "${metrics.warmLeads} ${strings.commonWarm.lowercase()}", HavnIconKey.TrendUp, Color(0xFFF97316), Modifier.weight(1f))
            }
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                KpiCard(strings.commonInquiries, metrics.inquiries.toString(), "${metrics.generalInquiries} ${strings.generalInquiry} · ${metrics.propertyInquiries} ${strings.propertyInquiry}", HavnIconKey.Leads, Color(0xFF64748B), Modifier.weight(1f))
                KpiCard(strings.tourSignals, metrics.tourSignals.toString(), strings.analyticsFromText, HavnIconKey.Calendar, Color(0xFF22C55E), Modifier.weight(1f))
            }
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                KpiCard(strings.conversion, "${metrics.conversion.oneDecimal()}%", strings.analyticsHotLeadsPerVisitors, HavnIconKey.Chart, Color(0xFFE11D48), Modifier.weight(1f))
                KpiCard(strings.analyticsAverageIntent, metrics.averageIntentLabel, strings.analyticsVisitorIntentScore, HavnIconKey.Today, Color(0xFF8B5CF6), Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun KpiCard(title: String, value: String, delta: String, icon: HavnIconKey, accent: Color, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier, border = BorderStroke(1.dp, theme.border)) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                HavnIcon(icon, contentDescription = title, tint = accent, modifier = Modifier.size(20.dp))
                Text(title, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, maxLines = 1, overflow = TextOverflow.Ellipsis)
            }
            Text(value, color = theme.primaryText, style = MaterialTheme.typography.headlineMedium)
            Text(delta, color = Color(0xFF4ADE80), style = MaterialTheme.typography.labelLarge)
        }
    }
}

@Composable
private fun FilterPill(label: String, icon: HavnIconKey, onClick: (() -> Unit)? = null) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        color = theme.card,
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border),
        modifier = if (onClick == null) Modifier else Modifier.clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 14.dp, vertical = 11.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            HavnIcon(icon, contentDescription = label, tint = theme.secondaryText, modifier = Modifier.size(17.dp))
            Text(label, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, maxLines = 1)
        }
    }
}

@Composable
private fun AnalyticsDatePickerDialog(
    title: String,
    initialValue: String?,
    fallbackValue: String?,
    onDismiss: () -> Unit,
    onConfirm: (String) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val initial = remember(initialValue, fallbackValue) { AnalyticsDateSelection.fromIso(initialValue ?: fallbackValue) }
    var year by remember(initialValue, fallbackValue) { mutableStateOf(initial.year) }
    var month by remember(initialValue, fallbackValue) { mutableStateOf(initial.month) }
    var day by remember(initialValue, fallbackValue) { mutableStateOf(initial.day.coerceAtMost(analyticsDaysInMonth(initial.year, initial.month))) }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(title) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    AnalyticsCalendarControlButton("<", onClick = {
                        if (month == 1) {
                            month = 12
                            year -= 1
                        } else {
                            month -= 1
                        }
                        day = day.coerceAtMost(analyticsDaysInMonth(year, month))
                    })
                    Text(
                        text = "${month.analyticsTwoDigits()} / $year",
                        modifier = Modifier.weight(1f),
                        color = theme.primaryText,
                        style = MaterialTheme.typography.titleLarge,
                        textAlign = TextAlign.Center
                    )
                    AnalyticsCalendarControlButton(">", onClick = {
                        if (month == 12) {
                            month = 1
                            year += 1
                        } else {
                            month += 1
                        }
                        day = day.coerceAtMost(analyticsDaysInMonth(year, month))
                    })
                }

                AnalyticsMonthGrid(
                    selectedDay = day,
                    daysInMonth = analyticsDaysInMonth(year, month),
                    onDaySelected = { day = it }
                )
            }
        },
        confirmButton = {
            TextButton(onClick = { onConfirm(analyticsIsoDate(year, month, day)) }) {
                Text(strings.commonDone)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(strings.commonCancel)
            }
        },
        containerColor = theme.surface,
        titleContentColor = theme.primaryText,
        textContentColor = theme.primaryText
    )
}

@Composable
private fun AnalyticsMonthGrid(
    selectedDay: Int,
    daysInMonth: Int,
    onDaySelected: (Int) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.xs)) {
        (1..daysInMonth).chunked(7).forEach { week ->
            Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.xs), modifier = Modifier.fillMaxWidth()) {
                week.forEach { currentDay ->
                    val selected = selectedDay == currentDay
                    Surface(
                        modifier = Modifier.weight(1f),
                        onClick = { onDaySelected(currentDay) },
                        shape = MaterialTheme.shapes.small,
                        color = if (selected) theme.primaryAccent else theme.surface,
                        contentColor = if (selected) theme.background else theme.primaryText,
                        border = BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border)
                    ) {
                        Box(modifier = Modifier.height(42.dp), contentAlignment = Alignment.Center) {
                            Text(currentDay.toString(), style = MaterialTheme.typography.labelLarge)
                        }
                    }
                }
                repeat(7 - week.size) {
                    Spacer(Modifier.weight(1f))
                }
            }
        }
    }
}

@Composable
private fun AnalyticsCalendarControlButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier.size(54.dp)
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier,
        onClick = onClick,
        shape = MaterialTheme.shapes.medium,
        color = theme.background.copy(alpha = 0.46f),
        contentColor = theme.primaryText,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Box(contentAlignment = Alignment.Center) {
            Text(text, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
        }
    }
}

@Composable
private fun FunnelCard(metrics: AnalyticsMetrics, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val language = LocalHavnLanguage.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(strings.propertyFunnel, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            Text(strings.propertyFunnelSubtitle, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
            val stages = listOf(
                FunnelStage(metrics.activeProperties.coerceAtLeast(0), analyticsGraphLabel("properties", language, strings), Color(0xFF2563EB)),
                FunnelStage(metrics.visitors.coerceAtLeast(0), analyticsGraphLabel("visitors", language, strings), Color(0xFF0EA5E9)),
                FunnelStage(metrics.interestedBuyers.coerceAtLeast(0), analyticsGraphLabel("interested", language, strings), Color(0xFF10B981)),
                FunnelStage(metrics.hotLeads.coerceAtLeast(0), analyticsGraphLabel("hot", language, strings), Color(0xFFF59E0B)),
                FunnelStage(metrics.tourSignals.coerceAtLeast(0), analyticsGraphLabel("tours", language, strings), Color(0xFF8B5CF6)),
                FunnelStage(metrics.offerSignals.coerceAtLeast(0), analyticsGraphLabel("offers", language, strings), Color(0xFFDB2777))
            )
            stages.forEachIndexed { index, stage ->
                FunnelBar(stage, widthFraction = (1f - index * 0.15f).coerceAtLeast(0.38f))
            }
        }
    }
}

@Composable
private fun FunnelBar(stage: FunnelStage, widthFraction: Float) {
    Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
        Surface(
            modifier = Modifier.fillMaxWidth(widthFraction).height(46.dp),
            color = stage.color.copy(alpha = 0.76f),
            shape = MaterialTheme.shapes.small
        ) {
            Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center) {
                Text(stage.value.toString(), color = Color.White, style = MaterialTheme.typography.titleMedium)
                Text(
                    stage.label,
                    color = Color.White,
                    style = MaterialTheme.typography.labelSmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
    }
}

@Composable
private fun TrendCard(series: List<ChartSeries>, dateRangeLabel: String, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text(strings.performanceOverTime, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                FilterPill(dateRangeLabel, HavnIconKey.Calendar)
            }
            LineChart(
                series = series,
                modifier = Modifier.fillMaxWidth().height(250.dp)
            )
        }
    }
}

@Composable
private fun LineChart(series: List<ChartSeries>, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val language = LocalHavnLanguage.current
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        FlowRow(
            horizontalArrangement = Arrangement.spacedBy(14.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
            modifier = Modifier.fillMaxWidth()
        ) {
            series.forEach {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    Surface(Modifier.size(8.dp), shape = CircleShape, color = it.color) {}
                    Text(
                        it.label.analyticsSeriesLabel(language, strings),
                        color = theme.secondaryText,
                        style = MaterialTheme.typography.labelSmall,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
        }
        Canvas(modifier) {
            val allValues = series.flatMap { it.values }
            val maxValue = allValues.maxOrNull()?.coerceAtLeast(1) ?: 1
            repeat(5) { index ->
                val y = size.height * index / 4f
                drawLine(theme.border.copy(alpha = 0.45f), Offset(0f, y), Offset(size.width, y), strokeWidth = 1f)
            }
            series.forEach { chartSeries ->
                val step = size.width / (chartSeries.values.size - 1).coerceAtLeast(1)
                val points = chartSeries.values.mapIndexed { index, value ->
                    Offset(index * step, size.height - value.toFloat() / maxValue.toFloat() * size.height)
                }
                points.zipWithNext().forEach { (a, b) ->
                    drawLine(chartSeries.color, a, b, strokeWidth = 3f, cap = StrokeCap.Round)
                }
                points.forEach { drawCircle(chartSeries.color, radius = 3.5f, center = it) }
            }
        }
    }
}

@Composable
private fun InsightCard(
    insight: String,
    aiJson: String,
    isLoading: Boolean,
    error: String?,
    onGenerate: () -> Unit,
    modifier: Modifier
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val language = LocalHavnLanguage.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                HavnIcon(HavnIconKey.Star, contentDescription = "AI Insights", tint = theme.primaryAccent, modifier = Modifier.size(20.dp))
                Text(strings.aiInsights, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            }
            if (aiJson.isNotBlank()) {
                AiOutputSections(
                    jsonText = aiJson,
                    keyOrder = analyticsAiOutputOrder,
                    titleForKey = { key -> key.analyticsAiSectionTitle(language) }
                )
            }
            error?.let {
                Text(it, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodyMedium)
            }
            HavnButton(
                if (isLoading) language.generatingAnalyticsInsightsLabel() else strings.generateAiInsights,
                onClick = onGenerate,
                loading = isLoading,
                showLoadingText = true,
                style = HavnButtonStyle.Secondary,
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = HavnIconKey.Star
            )
        }
    }
}

private val analyticsAiOutputOrder = listOf(
    "executive_summary",
    "agency_health_score",
    "key_wins",
    "risks",
    "top_properties",
    "underperforming_properties",
    "agent_coaching_insights",
    "recommended_actions"
)

@Composable
private fun AiOutputSections(
    jsonText: String,
    keyOrder: List<String>,
    titleForKey: (String) -> String
) {
    val theme = LocalHavnThemeTokens.current
    val sections = remember(jsonText, titleForKey) { parseAiOutputSections(jsonText, keyOrder, titleForKey) }
    if (sections.isNotEmpty()) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            sections.forEach { section ->
                Surface(
                    color = theme.background.copy(alpha = 0.35f),
                    shape = MaterialTheme.shapes.medium,
                    border = BorderStroke(1.dp, theme.border),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                        Text(section.title, color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
                        Text(section.body, color = theme.primaryText, style = MaterialTheme.typography.bodyMedium)
                    }
                }
            }
        }
    }
}

private data class AiOutputSection(val title: String, val body: String)

private fun parseAiOutputSections(
    jsonText: String,
    keyOrder: List<String>,
    titleForKey: (String) -> String
): List<AiOutputSection> {
    val jsonObject = runCatching { Json.parseToJsonElement(jsonText).jsonObject }.getOrNull() ?: return emptyList()
    val orderedKeys = keyOrder.filter { it in jsonObject.keys }
    return orderedKeys.mapNotNull { key ->
        val value = jsonObject[key] ?: return@mapNotNull null
        val body = value.aiTextValue(titleForKey).takeIf { it.isNotBlank() } ?: return@mapNotNull null
        AiOutputSection(titleForKey(key), body)
    }
}

private fun JsonElement.aiTextValue(titleForKey: (String) -> String): String = when (this) {
    is JsonPrimitive -> contentOrNull ?: toString()
    is JsonArray -> mapIndexed { index, item -> "${index + 1}. ${item.aiTextValue(titleForKey)}" }.joinToString("\n")
    is JsonObject -> entries.joinToString("\n") { (key, value) ->
        "${titleForKey(key)}: ${value.aiTextValue(titleForKey)}"
    }
}

private fun String.defaultAiTitle(): String =
    split("_", "-")
        .filter { it.isNotBlank() }
        .joinToString(" ") { part -> part.replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() } }

private fun HavnLanguage.generatingAnalyticsInsightsLabel(): String = when (this) {
    HavnLanguage.Slovak -> "Generujem..."
    else -> "Generating..."
}

private fun String.analyticsAiSectionTitle(language: HavnLanguage): String = when (language) {
    HavnLanguage.Slovak -> when (this) {
        "executive_summary" -> "Výkonný súhrn"
        "agency_health_score" -> "Skóre zdravia agentúry"
        "key_wins" -> "Hlavné úspechy"
        "risks" -> "Riziká"
        "top_properties" -> "Najvýkonnejšie nehnuteľnosti"
        "underperforming_properties" -> "Slabšie nehnuteľnosti"
        "agent_coaching_insights" -> "Poznatky pre koučing agentov"
        "recommended_actions" -> "Odporúčané kroky"
        else -> defaultAiTitle()
    }
    else -> defaultAiTitle()
}

private fun HavnLanguage.aiLanguageInstruction(): String = when (this) {
    HavnLanguage.Slovak -> "Slovak (Slovenčina, language code sk). Important: use Slovak, not Slovenian."
    HavnLanguage.Czech -> "Czech (Čeština, language code cs)."
    HavnLanguage.German -> "German (Deutsch, language code de)."
    HavnLanguage.French -> "French (Français, language code fr)."
    HavnLanguage.Spanish -> "Spanish (Español, language code es)."
    HavnLanguage.Italian -> "Italian (Italiano, language code it)."
    HavnLanguage.Croatian -> "Croatian (Hrvatski, language code hr)."
    HavnLanguage.Portuguese -> "Portuguese (Português, language code pt)."
    HavnLanguage.Dutch -> "Dutch (Nederlands, language code nl)."
    HavnLanguage.Polish -> "Polish (Polski, language code pl)."
    HavnLanguage.Hungarian -> "Hungarian (Magyar, language code hu)."
    HavnLanguage.Romanian -> "Romanian (Română, language code ro)."
    HavnLanguage.English -> "English (language code en)."
}

@Composable
private fun TopPropertiesCard(properties: List<Property>, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard(modifier = modifier) {
        BoxWithConstraints(Modifier.fillMaxWidth()) {
            val compact = maxWidth < 560.dp
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(strings.topPerformingProperties, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                    AnalyticsInfoDot()
                }
                if (!compact) {
                    AnalyticsHeaderRow(
                        first = strings.commonProperty,
                        columns = listOf(strings.visitors, strings.hotLeads, strings.conversion)
                    )
                }
                Column(verticalArrangement = Arrangement.spacedBy(0.dp)) {
                    properties.take(5).forEachIndexed { index, property ->
                        if (compact) {
                            PropertyPerformanceMobileRow(index + 1, property)
                        } else {
                            PropertyPerformanceRow(index + 1, property)
                        }
                        if (index < properties.take(5).lastIndex) AnalyticsDivider()
                    }
                }
                if (properties.isEmpty()) {
                    Text(strings.noPropertyAnalyticsYet, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                }
            }
        }
    }
}

@Composable
private fun PropertyPerformanceRow(rank: Int, property: Property) {
    val theme = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier.fillMaxWidth().heightIn(min = 66.dp).padding(vertical = 8.dp),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Surface(Modifier.size(24.dp), shape = CircleShape, color = rank.rankColor(theme)) {
            Box(contentAlignment = Alignment.Center) {
                Text(rank.toString(), color = theme.background, style = MaterialTheme.typography.labelSmall)
            }
        }
        PropertyAnalyticsThumbnail(property)
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(property.title, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, maxLines = 1, overflow = TextOverflow.Ellipsis)
            Text(property.displayCity, color = theme.secondaryText, style = MaterialTheme.typography.labelSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
            Text(property.askingPrice.analyticsPriceLabel(), color = theme.secondaryText, style = MaterialTheme.typography.labelSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
        AnalyticsNumberCell(property.stats.totalVisitors.toString())
        AnalyticsNumberCell(property.stats.hotLeads.toString())
        Column(horizontalAlignment = Alignment.End, modifier = Modifier.width(78.dp), verticalArrangement = Arrangement.spacedBy(3.dp)) {
            Text("${property.stats.conversionRate.oneDecimal()}%", color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
            MiniSparkline(property.id, Color(0xFF4ADE80), Modifier.fillMaxWidth().height(20.dp))
        }
    }
}

@Composable
private fun PropertyPerformanceMobileRow(rank: Int, property: Property) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().padding(vertical = 10.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Surface(Modifier.size(28.dp), shape = CircleShape, color = rank.rankColor(theme)) {
                Box(contentAlignment = Alignment.Center) {
                    Text(rank.toString(), color = theme.background, style = MaterialTheme.typography.labelMedium)
                }
            }
            PropertyAnalyticsThumbnail(property)
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(property.title, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(property.displayCity, color = theme.secondaryText, style = MaterialTheme.typography.labelSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
            }
        }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
            AnalyticsMetricChip(strings.visitors, property.stats.totalVisitors.toString(), Modifier.weight(1f))
            AnalyticsMetricChip(strings.hotLeads, property.stats.hotLeads.toString(), Modifier.weight(1f))
            AnalyticsMetricChip(strings.conversion, "${property.stats.conversionRate.oneDecimal()}%", Modifier.weight(1f))
        }
    }
}

@Composable
private fun AnalyticsHeaderRow(first: String, columns: List<String>) {
    val theme = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier.fillMaxWidth().padding(top = 4.dp, bottom = 2.dp),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(first.uppercase(), color = theme.secondaryText, style = MaterialTheme.typography.labelSmall, modifier = Modifier.weight(1f))
        columns.forEach { column ->
            Text(
                column.uppercase(),
                color = theme.secondaryText,
                style = MaterialTheme.typography.labelSmall,
                modifier = Modifier.width(if (column.length > 11) 96.dp else 72.dp),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

@Composable
private fun AnalyticsMetricChip(label: String, value: String, modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier.heightIn(min = 50.dp),
        shape = MaterialTheme.shapes.small,
        color = theme.background.copy(alpha = 0.38f),
        border = BorderStroke(1.dp, theme.border.copy(alpha = 0.55f))
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 8.dp),
            verticalArrangement = Arrangement.spacedBy(2.dp)
        ) {
            Text(value, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, maxLines = 1, overflow = TextOverflow.Ellipsis)
            Text(label, color = theme.secondaryText, style = MaterialTheme.typography.labelSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
    }
}

@Composable
private fun AnalyticsDivider() {
    val theme = LocalHavnThemeTokens.current
    Spacer(
        Modifier
            .fillMaxWidth()
            .height(1.dp)
            .background(theme.border.copy(alpha = 0.35f))
    )
}

@Composable
private fun AnalyticsInfoDot() {
    val theme = LocalHavnThemeTokens.current
    Surface(Modifier.size(15.dp), shape = CircleShape, color = Color.Transparent, border = BorderStroke(1.dp, theme.secondaryText.copy(alpha = 0.7f))) {
        Box(contentAlignment = Alignment.Center) {
            Text("i", color = theme.secondaryText, style = MaterialTheme.typography.labelSmall)
        }
    }
}

@Composable
private fun PropertyAnalyticsThumbnail(property: Property) {
    val theme = LocalHavnThemeTokens.current
    val photo = property.photos.firstOrNull { it.storagePath == property.heroPhotoPath }
        ?: property.photos.firstOrNull { it.isHero }
        ?: property.photos.firstOrNull()
    Surface(
        modifier = Modifier.size(width = 58.dp, height = 42.dp),
        shape = MaterialTheme.shapes.small,
        color = theme.surface,
        border = BorderStroke(1.dp, theme.border.copy(alpha = 0.55f))
    ) {
        val thumbnail = photo?.thumbnailDataUrl
        if (!thumbnail.isNullOrBlank()) {
            HavnDatabaseImage(
                dataUrl = thumbnail,
                previewDataUrl = havnPropertyPhotoPreviewSource(thumbnail, 88),
                contentDescription = property.title,
                contentScale = ContentScale.Crop,
                modifier = Modifier.fillMaxSize()
            )
        } else {
            Box(contentAlignment = Alignment.Center, modifier = Modifier.background(theme.background.copy(alpha = 0.6f))) {
                HavnIcon(HavnIconKey.Home, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(18.dp))
            }
        }
    }
}

@Composable
private fun AnalyticsNumberCell(value: String, width: androidx.compose.ui.unit.Dp = 72.dp) {
    val theme = LocalHavnThemeTokens.current
    Text(
        value,
        color = theme.primaryText,
        style = MaterialTheme.typography.bodyLarge,
        modifier = Modifier.width(width),
        maxLines = 1,
        overflow = TextOverflow.Ellipsis
    )
}

@Composable
private fun ProgressPill(percent: Int, modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    Surface(modifier = modifier.height(6.dp), shape = CircleShape, color = theme.border.copy(alpha = 0.42f)) {
        Box(Modifier.fillMaxWidth((percent / 100f).coerceIn(0f, 1f)).background(Color(0xFF4ADE80)))
    }
}

@Composable
private fun MiniSparkline(seed: String, color: Color, modifier: Modifier = Modifier) {
    val values = remember(seed) {
        val base = seed.fold(0) { acc, char -> acc + char.code }.coerceAtLeast(1)
        List(8) { index -> ((base / (index + 3)) + index * 11) % 38 + 18 }
    }
    Canvas(modifier) {
        val max = values.maxOrNull()?.toFloat()?.coerceAtLeast(1f) ?: 1f
        val step = size.width / (values.size - 1).coerceAtLeast(1)
        val points = values.mapIndexed { index, value ->
            Offset(index * step, size.height - value.toFloat() / max * size.height)
        }
        points.zipWithNext().forEach { (a, b) ->
            drawLine(color, a, b, strokeWidth = 2.2f, cap = StrokeCap.Round)
        }
    }
}

@Composable
private fun LeadQualityCard(hot: Int, warm: Int, cold: Int, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val total = (hot + warm + cold).coerceAtLeast(1)
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(strings.leadQualityBreakdown, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(18.dp)) {
                DonutChart(listOf(hot to Color(0xFFDB2777), warm to Color(0xFFF59E0B), cold to Color(0xFF3B82F6)), Modifier.size(150.dp))
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    LegendRow(strings.hotLeads, hot, hot * 100 / total, Color(0xFFDB2777))
                    LegendRow(strings.warmLeadsTitle, warm, warm * 100 / total, Color(0xFFF59E0B))
                    LegendRow(strings.coldLeadsTitle, cold, cold * 100 / total, Color(0xFF3B82F6))
                }
            }
        }
    }
}

@Composable
private fun DonutChart(values: List<Pair<Int, Color>>, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    Canvas(modifier) {
        val total = values.sumOf { it.first }.coerceAtLeast(1)
        var start = -90f
        values.forEach { (value, color) ->
            val sweep = value.toFloat() / total.toFloat() * 360f
            drawArc(color, startAngle = start, sweepAngle = sweep, useCenter = false, topLeft = Offset(12f, 12f), size = Size(size.width - 24f, size.height - 24f), style = Stroke(width = 28f, cap = StrokeCap.Butt))
            start += sweep
        }
        drawCircle(theme.background, radius = size.minDimension * 0.25f, center = center)
    }
}

@Composable
private fun LegendRow(label: String, value: Int, percent: Int, color: Color) {
    val theme = LocalHavnThemeTokens.current
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        Surface(Modifier.size(9.dp), shape = CircleShape, color = color) {}
        Text("$label $value ($percent%)", color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
    }
}

@Composable
private fun FollowUpCard(rate: Int, completed: Int, total: Int, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard(modifier = modifier) {
        Row(horizontalArrangement = Arrangement.spacedBy(18.dp), verticalAlignment = Alignment.CenterVertically) {
            DonutChart(listOf(rate to theme.primaryAccent, (100 - rate) to theme.border), Modifier.size(112.dp))
            Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                Text(strings.followUpPerformance, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                Text("$completed of $total leads followed up", color = theme.primaryText, style = MaterialTheme.typography.bodyLarge)
                Text("${(total - completed).coerceAtLeast(0)} leads still need follow-up", color = Color(0xFF4ADE80), style = MaterialTheme.typography.labelLarge)
            }
        }
    }
}

@Composable
private fun AgentPerformanceCard(rows: List<AgentPerformanceRow>, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard(modifier = modifier) {
        BoxWithConstraints(Modifier.fillMaxWidth()) {
            val compact = maxWidth < 620.dp
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(strings.agentPerformance, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                    AnalyticsInfoDot()
                }
                if (!compact) {
                    AnalyticsHeaderRow(
                        first = strings.agents,
                        columns = listOf(strings.analyticsOpenHousesLowercase, strings.hotLeads, strings.followUpPerformance, strings.conversion)
                    )
                }
                Column(verticalArrangement = Arrangement.spacedBy(0.dp)) {
                    rows.take(5).forEachIndexed { index, row ->
                        if (compact) {
                            AgentPerformanceMobileRow(row)
                        } else {
                            AgentRow(row.name, row.openHouses, row.hotLeads, row.followRate, row.conversion)
                        }
                        if (index < rows.take(5).lastIndex) AnalyticsDivider()
                    }
                }
                if (rows.isEmpty()) {
                    Text(strings.noPropertyAnalyticsYet, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                }
            }
        }
    }
}

@Composable
private fun AgentRow(name: String, openings: Int, hotLeads: Int, followRate: Int, conversion: Double) {
    val theme = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier.fillMaxWidth().heightIn(min = 46.dp).padding(vertical = 7.dp),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Surface(Modifier.size(30.dp), shape = CircleShape, color = theme.primaryAccent.copy(alpha = 0.22f)) {
            Box(contentAlignment = Alignment.Center) { HavnIcon(HavnIconKey.User, contentDescription = name, tint = theme.primaryAccent, modifier = Modifier.size(16.dp)) }
        }
        Text(name, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, modifier = Modifier.weight(1f), maxLines = 1, overflow = TextOverflow.Ellipsis)
        AnalyticsNumberCell(openings.toString())
        AnalyticsNumberCell(hotLeads.toString())
        Row(modifier = Modifier.width(96.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Text("$followRate%", color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
            ProgressPill(followRate, Modifier.weight(1f))
        }
        AnalyticsNumberCell("${conversion.oneDecimal()}%")
    }
}

@Composable
private fun AgentPerformanceMobileRow(row: AgentPerformanceRow) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().padding(vertical = 10.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Surface(Modifier.size(34.dp), shape = CircleShape, color = theme.primaryAccent.copy(alpha = 0.22f)) {
                Box(contentAlignment = Alignment.Center) {
                    HavnIcon(HavnIconKey.User, contentDescription = row.name, tint = theme.primaryAccent, modifier = Modifier.size(17.dp))
                }
            }
            Text(row.name, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, modifier = Modifier.weight(1f), maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
            AnalyticsMetricChip(strings.analyticsOpenHousesLowercase, row.openHouses.toString(), Modifier.weight(1f))
            AnalyticsMetricChip(strings.hotLeads, row.hotLeads.toString(), Modifier.weight(1f))
        }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
            AnalyticsMetricChip(strings.followUpPerformance, "${row.followRate}%", Modifier.weight(1f))
            AnalyticsMetricChip(strings.conversion, "${row.conversion.oneDecimal()}%", Modifier.weight(1f))
        }
    }
}

@Composable
private fun ChannelBreakdownCard(rows: List<SourceRow>, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(strings.followUpChannels, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                AnalyticsInfoDot()
            }
            rows.ifEmpty { listOf(SourceRow("No follow-ups yet", 0.0, theme.border)) }.forEach { row ->
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                    Surface(Modifier.size(24.dp), shape = CircleShape, color = row.color.copy(alpha = 0.18f)) {}
                    Text(row.label, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, modifier = Modifier.weight(1f), maxLines = 1, overflow = TextOverflow.Ellipsis)
                    Surface(modifier = Modifier.width(110.dp).height(7.dp), shape = CircleShape, color = theme.border.copy(alpha = 0.45f)) {
                        Box(Modifier.fillMaxWidth((row.percent / 100.0).toFloat().coerceIn(0.05f, 1f)).background(row.color))
                    }
                    Text("${row.percent.oneDecimal()}%", color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
                }
            }
        }
    }
}

@Composable
private fun AnalyticsPdfActions(
    generatedPdf: GeneratedAnalyticsExport?,
    uploadedPdf: HavnAttachment?,
    isUploadingPdf: Boolean,
    error: String?,
    onGenerate: () -> Unit,
    onOpenPdf: () -> Unit,
    onSendEmail: () -> Unit,
    onSendSms: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Text(strings.actions, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            error?.let { Text(it, color = theme.danger, style = MaterialTheme.typography.bodyMedium) }
            generatedPdf?.let { pdf ->
                Text(
                    if (uploadedPdf != null) strings.pdfSavedToFiles.withPdfFile(pdf.fileName) else strings.pdfSavingToFiles.withPdfFile(pdf.fileName),
                    color = if (uploadedPdf != null) theme.success else theme.secondaryText,
                    style = MaterialTheme.typography.labelLarge
                )
            }
            BoxWithConstraints(Modifier.fillMaxWidth()) {
                val compact = maxWidth < 720.dp
                if (compact) {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        HavnButton(strings.generatePdf, onClick = onGenerate, loading = isUploadingPdf, modifier = Modifier.fillMaxWidth(), leadingIcon = HavnIconKey.Reports)
                        HavnButton(strings.previewPdf, onClick = onOpenPdf, enabled = uploadedPdf != null, style = HavnButtonStyle.Secondary, modifier = Modifier.fillMaxWidth(), leadingIcon = HavnIconKey.Search)
                        HavnButton(strings.sendViaEmail, onClick = onSendEmail, enabled = uploadedPdf != null, style = HavnButtonStyle.Secondary, modifier = Modifier.fillMaxWidth(), leadingIcon = HavnIconKey.Mail)
                        HavnButton(strings.sendViaSms, onClick = onSendSms, enabled = uploadedPdf != null, style = HavnButtonStyle.Secondary, modifier = Modifier.fillMaxWidth(), leadingIcon = HavnIconKey.Message)
                    }
                } else {
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                        HavnButton(strings.generatePdf, onClick = onGenerate, loading = isUploadingPdf, modifier = Modifier.weight(1f), leadingIcon = HavnIconKey.Reports)
                        HavnButton(strings.previewPdf, onClick = onOpenPdf, enabled = uploadedPdf != null, style = HavnButtonStyle.Secondary, modifier = Modifier.weight(1f), leadingIcon = HavnIconKey.Search)
                        HavnButton(strings.sendViaEmail, onClick = onSendEmail, enabled = uploadedPdf != null, style = HavnButtonStyle.Secondary, modifier = Modifier.weight(1f), leadingIcon = HavnIconKey.Mail)
                        HavnButton(strings.sendViaSms, onClick = onSendSms, enabled = uploadedPdf != null, style = HavnButtonStyle.Secondary, modifier = Modifier.weight(1f), leadingIcon = HavnIconKey.Message)
                    }
                }
            }
            Text(
                strings.pdfIncludesAnalytics,
                color = theme.secondaryText,
                style = MaterialTheme.typography.labelMedium
            )
        }
    }
}

private data class FunnelStage(val value: Int, val label: String, val color: Color)
private data class ChartSeries(val label: String, val color: Color, val values: List<Int>)
private data class SourceRow(val label: String, val percent: Double, val color: Color)
private data class AgentPerformanceRow(
    val agentId: String,
    val name: String,
    val openHouses: Int,
    val visitors: Int,
    val hotLeads: Int,
    val followUpsSent: Int,
    val followRate: Int,
    val conversion: Double,
    val privateToursBooked: Int
)

private enum class AnalyticsDatePreset {
    Custom,
    LastDay,
    LastWeek,
    LastMonth,
    LastYear,
    AllTime;

    fun label(strings: HavnStrings): String = when (this) {
        Custom -> strings.analyticsCustomDate
        LastDay -> strings.analyticsLastDay
        LastWeek -> strings.analyticsLastWeek
        LastMonth -> strings.analyticsLastMonth
        LastYear -> strings.analyticsLastYear
        AllTime -> strings.analyticsAllTime
    }

    fun rangeFor(bounds: AnalyticsDateBounds): Pair<String?, String?> {
        val today = analyticsTodayIsoDate()
        val end = today.isoDayNumberOrNull() ?: bounds.latestDay ?: return null to null
        return when (this) {
            Custom -> bounds.earliestDate to bounds.latestDate
            LastDay -> end.analyticsDayNumberToIso() to end.analyticsDayNumberToIso()
            LastWeek -> (end - 6).analyticsDayNumberToIso() to end.analyticsDayNumberToIso()
            LastMonth -> (end - 29).analyticsDayNumberToIso() to end.analyticsDayNumberToIso()
            LastYear -> (end - 364).analyticsDayNumberToIso() to end.analyticsDayNumberToIso()
            AllTime -> null to null
        }
    }
}

private fun analyticsTodayIsoDate(): String =
    kotlin.time.Clock.System.now().toString().substringBefore("T")

private enum class AnalyticsDateTarget {
    From,
    To
}

private fun analyticsSelectedDateRangeLabel(
    preset: AnalyticsDatePreset,
    dateFrom: String?,
    dateTo: String?,
    strings: HavnStrings
): String {
    if (preset != AnalyticsDatePreset.Custom) return preset.label(strings)
    val fromLabel = dateFrom?.analyticsDateLabel()
    val toLabel = dateTo?.analyticsDateLabel()
    return when {
        fromLabel != null && toLabel != null && fromLabel != toLabel -> "$fromLabel - $toLabel"
        fromLabel != null -> fromLabel
        toLabel != null -> toLabel
        else -> strings.analyticsCustomDate
    }
}

private data class AnalyticsDateBounds(
    val earliestDay: Int?,
    val latestDay: Int?
) {
    val earliestDate: String?
        get() = earliestDay?.analyticsDayNumberToIso()
    val latestDate: String?
        get() = latestDay?.analyticsDayNumberToIso()
}

private data class AnalyticsDateSelection(
    val year: Int,
    val month: Int,
    val day: Int
) {
    companion object {
        fun fromIso(value: String?): AnalyticsDateSelection {
            val date = value?.substringBefore("T").orEmpty()
            val parts = date.split("-")
            return AnalyticsDateSelection(
                year = parts.getOrNull(0)?.toIntOrNull() ?: 2026,
                month = parts.getOrNull(1)?.toIntOrNull()?.coerceIn(1, 12) ?: 1,
                day = parts.getOrNull(2)?.toIntOrNull()?.coerceIn(1, 31) ?: 1
            )
        }
    }
}

private data class AnalyticsAgentOption(
    val agentId: String?,
    val label: String
)

private data class AnalyticsFilteredData(
    val properties: List<Property>,
    val openHouses: List<OpenHouse>,
    val leads: List<Lead>,
    val followUps: List<FollowUp>,
    val activityDateRange: String?
)

private data class AnalyticsMetrics(
    val dateRange: String,
    val activeProperties: Int,
    val openHouseCount: Int,
    val visitors: Int,
    val totalLeads: Int,
    val inquiries: Int,
    val generalInquiries: Int,
    val propertyInquiries: Int,
    val interestedBuyers: Int,
    val hotLeads: Int,
    val warmLeads: Int,
    val coldLeads: Int,
    val tourSignals: Int,
    val offerSignals: Int,
    val conversion: Double,
    val averageIntentLabel: String,
    val completedFollowUps: Int,
    val followUpRate: Int,
    val topProperties: List<Property>,
    val trendSeries: List<ChartSeries>,
    val insightText: String,
    val agentRows: List<AgentPerformanceRow>,
    val channelRows: List<SourceRow>
)

private fun buildAnalyticsAiCacheKey(
    agencyId: String?,
    agencyName: String,
    language: HavnLanguage,
    datePreset: AnalyticsDatePreset,
    dateFrom: String?,
    dateTo: String?,
    agentFilterId: String?,
    metrics: AnalyticsMetrics
): String {
    val raw = listOf(
        agencyId.orEmpty(),
        agencyName,
        language.name,
        datePreset.name,
        dateFrom.orEmpty(),
        dateTo.orEmpty(),
        agentFilterId.orEmpty(),
        metrics.activeProperties,
        metrics.openHouseCount,
        metrics.visitors,
        metrics.totalLeads,
        metrics.inquiries,
        metrics.hotLeads,
        metrics.completedFollowUps,
        metrics.tourSignals,
        metrics.offerSignals,
        metrics.topProperties.take(5).joinToString(",") { it.id }
    ).joinToString("|")
    return raw.hashCode().toUInt().toString(16)
}

private data class GeneratedAnalyticsExport(
    val fileName: String,
    val bytes: ByteArray
)

private fun Set<String>.toggleAnalyticsRecipient(value: String): Set<String> =
    if (value in this) this - value else this + value

private fun AnalyticsMetrics.generateAnalyticsXlsx(
    dateRange: String,
    agentLabel: String,
    agentName: String?,
    agencyName: String?,
    aiJson: String,
    strings: HavnStrings,
    language: HavnLanguage
): GeneratedAnalyticsExport {
    val safeAgency = agencyName?.takeIf { it.isNotBlank() } ?: "HAVN"
    val fileName = "HAVN-Analytics-${safeAgency.pdfSafeName()}-${dateRange.pdfSafeName()}.xlsx"
    val labels = AnalyticsPdfLabels.forLanguage(language)
    val aiRows = if (aiJson.isBlank()) {
        listOf(listOf(strings.aiInsights, labels.noAiInsights))
    } else {
        val aiSections = parseAiOutputSections(aiJson, analyticsAiOutputOrder) { it.defaultAiTitle() }
        if (aiSections.isEmpty()) {
            listOf(listOf(strings.aiInsights, aiJson))
        } else {
            listOf(listOf("Section", "Insight")) + aiSections.map { listOf(it.title, it.body) }
        }
    }
    val sheets = listOf(
        HavnXlsxSheet(
            labels.summaryMetrics,
            listOf(
                listOf("HAVN", strings.analyticsOverview),
                listOf(labels.dateRange, dateRange),
                listOf(labels.agentFilter, agentLabel),
                listOf(labels.preparedBy, agentName?.takeIf { it.isNotBlank() } ?: labels.notAvailable),
                listOf(labels.agency, safeAgency),
                listOf(labels.activeProperties, activeProperties),
                listOf(labels.openHousesHosted, openHouseCount),
                listOf(strings.visitors, visitors),
                listOf(labels.totalLeads, totalLeads),
                listOf(strings.interestedBuyers, interestedBuyers),
                listOf(strings.hotLeads, hotLeads),
                listOf(strings.warmLeadsTitle, warmLeads),
                listOf(strings.coldLeadsTitle, coldLeads),
                listOf(strings.tourSignals, tourSignals),
                listOf(strings.offerSignals, offerSignals),
                listOf(labels.hotConversion, conversion.oneDecimal()),
                listOf(labels.averageIntent, averageIntentLabel),
                listOf(strings.completedFollowUps, completedFollowUps),
                listOf(labels.followUpRate, followUpRate)
            )
        ),
        HavnXlsxSheet(
            strings.propertyFunnel,
            listOf(listOf("Stage", "Count")) + listOf(
                listOf(strings.properties, activeProperties),
                listOf(strings.visitors, visitors),
                listOf(strings.interestedBuyers, interestedBuyers),
                listOf(strings.hotLeads, hotLeads),
                listOf(strings.tourSignals, tourSignals),
                listOf(strings.offerSignals, offerSignals)
            )
        ),
        HavnXlsxSheet(
            strings.topPerformingProperties,
            listOf(listOf("Rank", strings.commonProperty, "City", labels.price, strings.visitors, strings.hotLeads, labels.conversion)) +
                topProperties.take(50).mapIndexed { index, property ->
                    listOf(index + 1, property.title, property.displayCity, property.askingPrice.analyticsPriceLabel(), property.stats.totalVisitors, property.stats.hotLeads, property.stats.conversionRate.oneDecimal())
                }
        ),
        HavnXlsxSheet(
            strings.agentPerformance,
            listOf(listOf(strings.agents, labels.openHouses, strings.visitors, strings.hotLeads, strings.followUpsSent, labels.followUpRate, labels.conversion)) +
                agentRows.map { row ->
                    listOf(row.name, row.openHouses, row.visitors, row.hotLeads, row.followUpsSent, row.followRate, row.conversion.oneDecimal())
                }
        ),
        HavnXlsxSheet(
            labels.conversionBySource,
            listOf(listOf("Source", labels.conversion)) + channelRows.map { row -> listOf(row.label, row.percent.oneDecimal()) }
        ),
        HavnXlsxSheet(strings.aiInsights, aiRows),
        HavnXlsxSheet(labels.footer, listOf(listOf(labels.reviewBeforeSharing)))
    )
    return GeneratedAnalyticsExport(fileName, buildHavnXlsx(sheets))
}

private fun buildA4AnalyticsPdf(lines: List<String>, strings: HavnStrings, labels: AnalyticsPdfLabels): ByteArray {
    val pages = lines.chunked(42).ifEmpty { listOf(listOf("HAVN ${strings.analyticsOverview}")) }
    val objects = mutableListOf<String>()
    fun addObject(value: String): Int {
        objects += value
        return objects.size
    }
    val catalogId = addObject("<< /Type /Catalog /Pages 2 0 R >>")
    val pagesId = addObject("")
    val fontId = addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding << /Type /Encoding /BaseEncoding /WinAnsiEncoding /Differences [${analyticsPdfEncodingDifferences()}] >> >>")
    val pageIds = mutableListOf<Int>()
    pages.forEachIndexed { pageIndex, pageLines ->
        val content = buildString {
            append("q\nBT\n")
            append("/F1 16 Tf\n0.74 0.52 0.18 rg\n54 800 Td\n<${"HAVN".pdfTextHex()}> Tj\n")
            append("/F1 10 Tf\n0.85 0.87 0.90 rg\n0 -18 Td\n<${"${strings.analyticsOverview} - ${labels.page} ${pageIndex + 1} ${labels.of} ${pages.size}".pdfTextHex()}> Tj\n")
            append("/F1 10 Tf\n0.06 0.07 0.09 rg\n0 -26 Td\n")
            pageLines.forEach { rawLine ->
                val chunks = rawLine.pdfLineChunks()
                if (chunks.isEmpty()) append("0 -12 Td\n") else chunks.forEach { chunk ->
                    append("<").append(chunk.pdfTextHex()).append("> Tj\n")
                    append("0 -14 Td\n")
                }
            }
            append("/F1 8 Tf\n0.45 0.45 0.45 rg\n0 -12 Td\n<${"HAVN Real Estate App - Analytics briefing".pdfTextHex()}> Tj\n")
            append("ET\nQ\n")
        }
        val contentId = addObject("<< /Length ${content.encodeToByteArray().size} >>\nstream\n$content\nendstream")
        val pageId = addObject("<< /Type /Page /Parent $pagesId 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 $fontId 0 R >> >> /Contents $contentId 0 R >>")
        pageIds += pageId
    }
    objects[pagesId - 1] = "<< /Type /Pages /Kids [${pageIds.joinToString(" ") { "$it 0 R" }}] /Count ${pageIds.size} >>"
    val pdf = StringBuilder("%PDF-1.4\n")
    val offsets = mutableListOf(0)
    objects.forEachIndexed { index, obj ->
        offsets += pdf.length
        pdf.append(index + 1).append(" 0 obj\n").append(obj).append("\nendobj\n")
    }
    val xrefOffset = pdf.length
    pdf.append("xref\n0 ").append(objects.size + 1).append("\n")
    pdf.append("0000000000 65535 f \n")
    offsets.drop(1).forEach { offset -> pdf.append(offset.toString().padStart(10, '0')).append(" 00000 n \n") }
    pdf.append("trailer\n<< /Size ").append(objects.size + 1).append(" /Root $catalogId 0 R >>\n")
    pdf.append("startxref\n").append(xrefOffset).append("\n%%EOF")
    return pdf.toString().encodeToByteArray()
}

private fun String.pdfLineChunks(maxLength: Int = 86): List<String> {
    if (isBlank()) return emptyList()
    val words = split(Regex("\\s+")).filter { it.isNotBlank() }
    val lines = mutableListOf<String>()
    var current = StringBuilder()
    words.forEach { word ->
        if (current.isEmpty()) current.append(word)
        else if (current.length + 1 + word.length <= maxLength) current.append(' ').append(word)
        else {
            lines += current.toString()
            current = StringBuilder(word)
        }
    }
    if (current.isNotEmpty()) lines += current.toString()
    return lines
}

private val analyticsPdfGlyphBytes: Map<Char, Pair<Int, String>> = listOf(
    'Á' to (128 to "Aacute"),
    'á' to (129 to "aacute"),
    'Ä' to (130 to "Adieresis"),
    'ä' to (131 to "adieresis"),
    'Č' to (132 to "Ccaron"),
    'č' to (133 to "ccaron"),
    'Ď' to (134 to "Dcaron"),
    'ď' to (135 to "dcaron"),
    'É' to (136 to "Eacute"),
    'é' to (137 to "eacute"),
    'Í' to (138 to "Iacute"),
    'í' to (139 to "iacute"),
    'Ĺ' to (140 to "Lacute"),
    'ĺ' to (141 to "lacute"),
    'Ľ' to (142 to "Lcaron"),
    'ľ' to (143 to "lcaron"),
    'Ň' to (144 to "Ncaron"),
    'ň' to (145 to "ncaron"),
    'Ó' to (146 to "Oacute"),
    'ó' to (147 to "oacute"),
    'Ô' to (148 to "Ocircumflex"),
    'ô' to (149 to "ocircumflex"),
    'Ŕ' to (150 to "Racute"),
    'ŕ' to (151 to "racute"),
    'Š' to (152 to "Scaron"),
    'š' to (153 to "scaron"),
    'Ť' to (154 to "Tcaron"),
    'ť' to (155 to "tcaron"),
    'Ú' to (156 to "Uacute"),
    'ú' to (157 to "uacute"),
    'Ý' to (158 to "Yacute"),
    'ý' to (159 to "yacute"),
    'Ž' to (160 to "Zcaron"),
    'ž' to (161 to "zcaron")
).associate { it.first to it.second }

private fun analyticsPdfEncodingDifferences(): String =
    analyticsPdfGlyphBytes.values
        .sortedBy { it.first }
        .joinToString(" ") { (byte, glyph) -> "$byte /$glyph" }

private fun String.pdfTextHex(): String =
    flatMap { char ->
        val byte = when {
            char.code in 32..126 -> char.code
            char == '\n' || char == '\r' || char == '\t' -> 32
            char == '–' || char == '—' -> '-'.code
            char == '“' || char == '”' -> '"'.code
            char == '‘' || char == '’' -> '\''.code
            char == '€' -> 'E'.code
            else -> analyticsPdfGlyphBytes[char]?.first ?: '?'.code
        }
        listOf(byte.toString(16).padStart(2, '0').uppercase())
    }.joinToString("")

private fun String.pdfSafeName(): String =
    trim()
        .ifBlank { "analytics" }
        .replace(Regex("[^A-Za-z0-9._-]+"), "-")
        .trim('-')
        .take(80)
        .ifBlank { "analytics" }

private fun String.withPdfFile(fileName: String): String =
    replace("%s", fileName)

private data class AnalyticsPdfLabels(
    val dateRange: String,
    val agentFilter: String,
    val preparedBy: String,
    val agency: String,
    val summaryMetrics: String,
    val activeProperties: String,
    val openHousesHosted: String,
    val totalLeads: String,
    val hotConversion: String,
    val averageIntent: String,
    val followUpRate: String,
    val conversion: String,
    val price: String,
    val noAgentRows: String,
    val openHouses: String,
    val followUp: String,
    val conversionBySource: String,
    val noChannelData: String,
    val noAiInsights: String,
    val footer: String,
    val reviewBeforeSharing: String,
    val notAvailable: String,
    val page: String,
    val of: String
) {
    companion object {
        fun forLanguage(language: HavnLanguage): AnalyticsPdfLabels = when (language) {
            HavnLanguage.Slovak -> AnalyticsPdfLabels(
                dateRange = "Obdobie",
                agentFilter = "Filter agenta",
                preparedBy = "Pripravil",
                agency = "Agentúra",
                summaryMetrics = "Súhrnné metriky",
                activeProperties = "Aktívne nehnuteľnosti",
                openHousesHosted = "Uskutočnené obhliadky",
                totalLeads = "Leady spolu",
                hotConversion = "Konverzia horúcich leadov",
                averageIntent = "Priemerný záujem",
                followUpRate = "Miera follow-upov",
                conversion = "Konverzia",
                price = "Cena",
                noAgentRows = "Nie sú dostupné žiadne riadky výkonu agentov.",
                openHouses = "obhliadok",
                followUp = "follow-up",
                conversionBySource = "Konverzia podľa zdroja",
                noChannelData = "Nie sú dostupné žiadne dáta kanálov.",
                noAiInsights = "AI poznatky ešte neboli vygenerované.",
                footer = "Pätička HAVN",
                reviewBeforeSharing = "Analytický report je vygenerovaný z aktuálnej aktivity HAVN. Pred zdieľaním ho skontrolujte.",
                notAvailable = "Nie je dostupné",
                page = "strana",
                of = "z"
            )
            else -> AnalyticsPdfLabels(
                dateRange = "Date range",
                agentFilter = "Agent filter",
                preparedBy = "Prepared by",
                agency = "Agency",
                summaryMetrics = "Summary Metrics",
                activeProperties = "Active properties",
                openHousesHosted = "Open houses hosted",
                totalLeads = "Total leads",
                hotConversion = "Hot conversion",
                averageIntent = "Average intent",
                followUpRate = "Follow-up rate",
                conversion = "Conversion",
                price = "Price",
                noAgentRows = "No agent performance rows were available.",
                openHouses = "open houses",
                followUp = "follow-up",
                conversionBySource = "Conversion by Source",
                noChannelData = "No channel data was available.",
                noAiInsights = "AI insights have not been generated yet.",
                footer = "HAVN footer",
                reviewBeforeSharing = "Analytics report generated from current HAVN activity. Review before sharing.",
                notAvailable = "Not available",
                page = "Page",
                of = "of"
            )
        }
    }
}

private fun buildAgentOptions(
    members: List<AgencyMember>,
    currentAgentId: String?,
    strings: HavnStrings
): List<AnalyticsAgentOption> {
    val memberOptions = members
        .mapNotNull { member ->
            member.agentId?.let { AnalyticsAgentOption(it, member.fullName.ifBlank { member.email }) }
        }
        .distinctBy { it.agentId }
    return buildList {
        add(AnalyticsAgentOption(null, strings.allAgents))
        addAll(memberOptions)
        if (currentAgentId != null && memberOptions.none { it.agentId == currentAgentId }) {
            add(AnalyticsAgentOption(currentAgentId, strings.me))
        }
    }
}

private fun List<AnalyticsAgentOption>.nextAgentId(current: String?): String? {
    if (isEmpty()) return null
    val index = indexOfFirst { it.agentId == current }.takeIf { it >= 0 } ?: 0
    return get((index + 1) % size).agentId
}

private fun filterAnalyticsData(
    dateFrom: String?,
    dateTo: String?,
    agentId: String?,
    properties: List<Property>,
    openHouses: List<OpenHouse>,
    leads: List<Lead>,
    followUps: List<FollowUp>
): AnalyticsFilteredData {
    val fromDay = dateFrom.isoDayNumberOrNull()
    val toDay = dateTo.isoDayNumberOrNull()
    fun inDate(value: String?): Boolean {
        val day = value.isoDayNumberOrNull() ?: return fromDay == null && toDay == null
        return (fromDay == null || day >= fromDay) && (toDay == null || day <= toDay)
    }

    val agentPropertyIds = if (agentId == null) {
        properties.map { it.id }.toSet()
    } else {
        properties.filter {
            it.agentId == agentId || it.ownerAgentId == agentId || it.assignedAgentId == agentId
        }.map { it.id }.toSet()
    }
    val agentOpenHouses = openHouses.filter { opening ->
        (agentId == null || opening.hostAgentId == agentId || opening.propertyId in agentPropertyIds) &&
            inDate(opening.startsAt)
    }
    val visibleOpenHouseIds = agentOpenHouses.map { it.id }.toSet()
    val visiblePropertyIdsFromOpenings = agentOpenHouses.map { it.propertyId }.toSet()
    val filteredLeads = leads.filter { lead ->
        (lead.isBuyerInquiry || lead.openHouseId in visibleOpenHouseIds || lead.propertyId in agentPropertyIds) &&
            inDate(lead.checkedInAt)
    }
    val visibleVisitorIds = filteredLeads.mapNotNull { it.visitorId }.toSet()
    val filteredFollowUps = followUps.filter { followUp ->
        followUp.visitorId in visibleVisitorIds &&
            inDate(followUp.completedAt ?: followUp.dueAt)
    }
    val visiblePropertyIds = (agentPropertyIds.takeIf { agentId != null } ?: properties.map { it.id }.toSet())
        .intersect(
            (visiblePropertyIdsFromOpenings + filteredLeads.map { it.propertyId }).takeIf { it.isNotEmpty() }
                ?: (agentPropertyIds.takeIf { agentId != null } ?: properties.map { it.id }.toSet())
        )
    val filteredProperties = properties.filter { property ->
        property.id in visiblePropertyIds &&
            (fromDay == null && toDay == null || property.id in visiblePropertyIdsFromOpenings || property.id in filteredLeads.map { it.propertyId }.toSet() || inDate(property.createdAt))
    }
    val activityDates = (agentOpenHouses.map { it.startsAt } + filteredLeads.map { it.checkedInAt })
        .filter { inDate(it) }
    return AnalyticsFilteredData(
        properties = filteredProperties,
        openHouses = agentOpenHouses,
        leads = filteredLeads,
        followUps = filteredFollowUps,
        activityDateRange = activityDates.dateRangeLabelFromStrings()
    )
}

private fun analyticsActivityDateBounds(
    openHouses: List<OpenHouse>,
    leads: List<Lead>,
    followUps: List<FollowUp>
): AnalyticsDateBounds {
    val days = listOf(
        openHouses.mapNotNull { it.startsAt.isoDayNumberOrNull() },
        leads.mapNotNull { it.checkedInAt.isoDayNumberOrNull() },
        followUps.mapNotNull { (it.completedAt ?: it.dueAt).isoDayNumberOrNull() }
    ).flatten()
    return AnalyticsDateBounds(
        earliestDay = days.minOrNull(),
        latestDay = days.maxOrNull()
    )
}

private fun buildAnalyticsMetrics(
    properties: List<Property>,
    openHouses: List<OpenHouse>,
    leads: List<Lead>,
    followUps: List<FollowUp>,
    members: List<AgencyMember>,
    dateFrom: String?,
    dateTo: String?,
    dateRangeLabel: String
): AnalyticsMetrics {
    val visitorCountFromOpenHouses = openHouses.sumOf { it.stats.visitorCount }
    val inquiryCount = leads.count { it.isInquiry }
    val visitors = visitorCountFromOpenHouses.coerceAtLeast(leads.size)
    val hotLeads = leads.count { it.temperature == LeadTemperature.Hot }
        .coerceAtLeast(openHouses.sumOf { it.stats.hotCount })
    val warmLeads = leads.count { it.temperature == LeadTemperature.Warm }
        .coerceAtLeast(openHouses.sumOf { it.stats.warmCount })
    val coldLeads = leads.count { it.temperature == LeadTemperature.Cold }
        .coerceAtLeast(openHouses.sumOf { it.stats.coldCount })
    val interested = (hotLeads + warmLeads).coerceAtMost(visitors)
    val tourSignals = countTextSignals(leads, followUps, listOf("tour", "showing", "second showing", "private"))
    val offerSignals = countTextSignals(leads, followUps, listOf("offer", "bid", "contract"))
    val completedFollowUps = followUps.count { it.isSentMessageFollowUp }
    val followUpRate = if (leads.isEmpty()) 0 else (completedFollowUps.toDouble() / leads.size.toDouble() * 100).roundToInt().coerceIn(0, 100)
    val conversion = if (visitors == 0) 0.0 else hotLeads.toDouble() / visitors.toDouble() * 100.0
    val averageIntent = leads.mapNotNull { it.intentScore }.averageOrNull()
    val topProperties = properties.sortedByDescending {
        it.stats.totalVisitors + it.stats.hotLeads * 5 + it.stats.followUpsSent * 2
    }.take(5)
    val openHousesById = openHouses.associateBy { it.id }
    val memberNameByAgent = members.mapNotNull { member ->
        member.agentId?.let { it to member.fullName }
    }.toMap()
    val visitorToOpenHouse = leads.mapNotNull { lead ->
        val openHouseId = lead.openHouseId ?: return@mapNotNull null
        lead.visitorId?.let { it to openHouseId }
    }.toMap()
    val agentRows = openHouses.groupBy { it.hostAgentId }
        .map { (agentId, hosted) ->
            val hostedIds = hosted.map { it.id }.toSet()
            val hostedLeads = leads.filter { it.openHouseId in hostedIds }
            val hostedVisitorIds = hostedLeads.mapNotNull { it.visitorId }.toSet()
            val hostedFollowUps = followUps.filter { it.visitorId in hostedVisitorIds }
            val hostedVisitors = hosted.sumOf { it.stats.visitorCount }.coerceAtLeast(hostedLeads.size)
            val hostedHot = hostedLeads.count { it.temperature == LeadTemperature.Hot }.coerceAtLeast(hosted.sumOf { it.stats.hotCount })
            AgentPerformanceRow(
                agentId = agentId.ifBlank { "unknown_agent" },
                name = memberNameByAgent[agentId] ?: "Agent",
                openHouses = hosted.size,
                visitors = hostedVisitors,
                hotLeads = hostedHot,
                followUpsSent = hostedFollowUps.count { it.isSentMessageFollowUp },
                followRate = if (hostedLeads.isEmpty()) 0 else (hostedFollowUps.count { it.isSentMessageFollowUp }.toDouble() / hostedLeads.size.toDouble() * 100).roundToInt().coerceIn(0, 100),
                conversion = if (hostedVisitors == 0) 0.0 else hostedHot.toDouble() / hostedVisitors.toDouble() * 100.0,
                privateToursBooked = countTextSignals(hostedLeads, hostedFollowUps, listOf("tour", "showing", "second showing", "private"))
            )
        }
        .sortedByDescending { it.hotLeads + it.openHouses }
    val trend = buildTrendSeries(openHouses, leads, followUps, visitorToOpenHouse, dateFrom, dateTo)
    val channelRows = buildChannelRows(leads)
    val insight = buildInsight(visitors, hotLeads, warmLeads, completedFollowUps, followUpRate, topProperties.firstOrNull())

    return AnalyticsMetrics(
        dateRange = dateRangeLabel,
        activeProperties = properties.count { property ->
            property.id in openHouses.map { it.propertyId }.toSet() ||
                property.id in leads.map { it.propertyId }.toSet() ||
                property.stats.totalVisitors > 0 ||
                property.stats.hotLeads > 0
        }.coerceAtLeast(properties.size.takeIf { it > 0 && openHouses.isEmpty() && leads.isEmpty() } ?: 0),
        openHouseCount = openHouses.size,
        visitors = visitors,
        totalLeads = leads.size,
        inquiries = inquiryCount,
        generalInquiries = leads.count { it.isBuyerInquiry },
        propertyInquiries = leads.count { it.isInquiry && !it.isBuyerInquiry },
        interestedBuyers = interested,
        hotLeads = hotLeads,
        warmLeads = warmLeads,
        coldLeads = coldLeads,
        tourSignals = tourSignals,
        offerSignals = offerSignals,
        conversion = conversion,
        averageIntentLabel = averageIntent?.let { "${it.oneDecimal()}/5" } ?: "No data",
        completedFollowUps = completedFollowUps,
        followUpRate = followUpRate,
        topProperties = topProperties,
        trendSeries = trend,
        insightText = insight,
        agentRows = agentRows,
        channelRows = channelRows
    )
}

private fun AnalyticsMetrics.toAiInput(
    language: String,
    agencyName: String,
    filteredData: AnalyticsFilteredData
): AnalyticsAiInput {
    val periodParts = (filteredData.activityDateRange ?: dateRange)
        .split(" - ")
        .map { it.trim() }
        .filter { it.isNotBlank() }
    val openHousesByProperty = filteredData.openHouses.groupBy { it.propertyId }
    val leadsByProperty = filteredData.leads.groupBy { it.propertyId }
    val followUpsByVisitor = filteredData.followUps.groupBy { it.visitorId }
    val propertyRows = topProperties.map { property ->
        val propertyOpenHouses = openHousesByProperty[property.id].orEmpty()
        val propertyLeads = leadsByProperty[property.id].orEmpty()
        val visitorIds = propertyLeads.mapNotNull { it.visitorId }.toSet()
        val propertyFollowUps = visitorIds.flatMap { followUpsByVisitor[it].orEmpty() }
        val privateTours = countTextSignals(propertyLeads, propertyFollowUps, listOf("tour", "showing", "second showing", "private"))
        val visitors = property.stats.totalVisitors.coerceAtLeast(propertyLeads.size).coerceAtLeast(propertyOpenHouses.sumOf { it.stats.visitorCount })
        val hot = property.stats.hotLeads.coerceAtLeast(propertyLeads.count { it.temperature == LeadTemperature.Hot })
        AnalyticsAiPropertyPerformance(
            propertyId = property.id,
            address = listOf(property.addressLine1, property.city, property.region).filter { it.isNotBlank() }.joinToString(", "),
            openHouses = property.stats.openingCount.coerceAtLeast(propertyOpenHouses.size),
            visitors = visitors,
            leads = propertyLeads.size,
            hotLeads = hot,
            privateTours = privateTours,
            sellerConfidenceScore = analyticsSellerConfidenceScore(visitors, hot, privateTours, property.stats.followUpsSent)
        )
    }
    val commonNotes = buildList {
        addAll(filteredData.leads.mapNotNull { it.notes?.takeIf { note -> note.isNotBlank() } }.take(8))
        addAll(filteredData.followUps.mapNotNull { it.body?.takeIf { body -> body.isNotBlank() } }.take(8))
        add("Seller reports generated is not available from the current Analytics Overview data.")
        add("Average follow-up time is not available from the current Analytics Overview data.")
        add("Previous-period trend percentages are not available from the current Analytics Overview data.")
    }
    return AnalyticsAiInput(
        language = language,
        agencyName = agencyName.ifBlank { "HAVN agency" },
        reportPeriodStart = periodParts.firstOrNull() ?: "Not available",
        reportPeriodEnd = periodParts.getOrNull(1) ?: periodParts.firstOrNull() ?: "Not available",
        summaryMetrics = AnalyticsAiSummaryMetrics(
            activeProperties = activeProperties,
            openHousesHosted = openHouseCount,
            totalVisitors = visitors,
            totalLeads = totalLeads,
            inquiries = inquiries,
            generalInquiries = filteredData.leads.count { it.isBuyerInquiry },
            propertyInquiries = filteredData.leads.count { it.isInquiry && !it.isBuyerInquiry },
            leadSources = filteredData.leads.groupingBy { it.source?.trim()?.lowercase()?.takeIf { source -> source.isNotBlank() } ?: "unspecified" }.eachCount(),
            hotLeads = hotLeads,
            warmLeads = warmLeads,
            coldLeads = coldLeads,
            privateToursRequested = tourSignals,
            followUpsSent = completedFollowUps,
            sellerReportsGenerated = 0,
            offersRecorded = offerSignals
        ),
        conversionFunnel = AnalyticsAiConversionFunnel(
            visitors = visitors,
            leads = totalLeads,
            hotLeads = hotLeads,
            privateTours = tourSignals,
            offers = offerSignals
        ),
        propertyPerformance = propertyRows,
        agentPerformance = agentRows.map {
            AnalyticsAiAgentPerformance(
                agentId = it.agentId,
                name = it.name,
                openHousesHosted = it.openHouses,
                visitors = it.visitors,
                hotLeads = it.hotLeads,
                followUpsSent = it.followUpsSent,
                averageFollowupTimeHours = null,
                privateToursBooked = it.privateToursBooked
            )
        },
        recentTrends = AnalyticsAiRecentTrends(
            visitorChangeVsPreviousPeriodPercent = null,
            hotLeadChangeVsPreviousPeriodPercent = null,
            privateTourChangeVsPreviousPeriodPercent = null,
            followupCompletionChangePercent = null
        ),
        commonAgentNotes = commonNotes.distinct().take(16)
    )
}

private fun analyticsSellerConfidenceScore(visitors: Int, hotLeads: Int, privateTours: Int, followUps: Int): Int =
    (35 + visitors.coerceAtMost(40) + hotLeads * 3 + privateTours * 4 + followUps.coerceAtMost(20)).coerceIn(35, 96)

private fun buildTrendSeries(
    openHouses: List<OpenHouse>,
    leads: List<Lead>,
    followUps: List<FollowUp>,
    visitorToOpenHouse: Map<String, String>,
    dateFrom: String?,
    dateTo: String?
): List<ChartSeries> {
    val selectedStart = dateFrom.isoDayNumberOrNull()
    val selectedEnd = dateTo.isoDayNumberOrNull()
    val activityDays = buildList {
        addAll(openHouses.mapNotNull { it.startsAt.isoDayNumberOrNull() })
        addAll(leads.mapNotNull { it.checkedInAt.isoDayNumberOrNull() })
        addAll(followUps.mapNotNull { it.eventDate.isoDayNumberOrNull() })
    }
    val startDay = selectedStart ?: activityDays.minOrNull()
    val endDay = selectedEnd ?: activityDays.maxOrNull()
    val days = if (startDay != null && endDay != null && startDay <= endDay) {
        (startDay..endDay).toList()
    } else {
        (0 until 30).toList()
    }
    val openHousesByDay = openHouses.groupBy { it.startsAt.isoDayNumberOrNull() }
    val leadsByDay = leads.groupBy { it.checkedInAt.isoDayNumberOrNull() }
    val tourOpeningIds = followUps
        .filter { it.textContainsAny(listOf("tour", "showing", "private")) }
        .mapNotNull { visitorToOpenHouse[it.visitorId] }
        .groupingBy { it }
        .eachCount()
    val completedFollowUpsByOpening = followUps
        .filter { it.isSentMessageFollowUp }
        .mapNotNull { followUp -> visitorToOpenHouse[followUp.visitorId] }
        .groupingBy { it }
        .eachCount()
    return listOf(
        ChartSeries("Visitors", Color(0xFF3B82F6), days.map { day ->
            openHousesByDay[day].orEmpty().sumOf { it.stats.visitorCount }
                .coerceAtLeast(leadsByDay[day].orEmpty().size)
        }),
        ChartSeries("Hot Leads", Color(0xFF22C55E), days.map { day ->
            leadsByDay[day].orEmpty().count { it.temperature == LeadTemperature.Hot }
                .coerceAtLeast(openHousesByDay[day].orEmpty().sumOf { it.stats.hotCount })
        }),
        ChartSeries("Tour Signals", Color(0xFFF59E0B), days.map { day ->
            openHousesByDay[day].orEmpty().sumOf { tourOpeningIds[it.id] ?: 0 }
        }),
        ChartSeries("Follow-Ups", Color(0xFF8B5CF6), days.map { day ->
            openHousesByDay[day].orEmpty().sumOf { completedFollowUpsByOpening[it.id] ?: 0 }
        })
    )
}

private fun buildChannelRows(leads: List<Lead>): List<SourceRow> {
    val palette = listOf(Color(0xFFD6AA4F), Color(0xFF22C55E), Color(0xFF3B82F6), Color(0xFFA855F7), Color(0xFFF97316), Color(0xFF64748B))
    val counts = leads.groupingBy { it.source?.trim()?.replace('_', ' ')?.takeIf { value -> value.isNotBlank() } ?: "Unspecified" }.eachCount()
    val total = counts.values.sum().coerceAtLeast(1)
    return counts.entries.sortedByDescending { it.value }.mapIndexed { index, entry ->
        SourceRow(entry.key.replaceFirstChar { it.uppercase() }, entry.value.toDouble() / total.toDouble() * 100.0, palette[index % palette.size])
    }
}

private fun buildInsight(
    visitors: Int,
    hotLeads: Int,
    warmLeads: Int,
    completedFollowUps: Int,
    followUpRate: Int,
    topProperty: Property?
): String {
    val propertyLine = topProperty?.let { "${it.title} is currently the strongest property by visitor and hot-lead activity. " } ?: ""
    return when {
        visitors == 0 -> "No visitor registrations are available yet. Analytics will become more useful after the next open house sign-ins."
        hotLeads > 0 -> "${propertyLine}$hotLeads hot leads and $warmLeads warm leads are visible from registered buyer activity. Sent follow-up rate is $followUpRate%."
        completedFollowUps > 0 -> "${propertyLine}Buyer activity is early, but $completedFollowUps SMS/email follow-ups have already been sent. Watch hot-lead conversion after the next event."
        else -> "${propertyLine}$visitors visitors are registered. Start follow-ups to create stronger conversion signals."
    }
}

private fun countTextSignals(leads: List<Lead>, followUps: List<FollowUp>, terms: List<String>): Int {
    val leadSignals = leads.count { lead ->
        lead.notes?.containsAny(terms) == true
    }
    val followUpSignals = followUps.count { it.textContainsAny(terms) }
    return leadSignals + followUpSignals
}

private fun FollowUp.textContainsAny(terms: List<String>): Boolean =
    subject.containsAny(terms) || body?.containsAny(terms) == true

private val FollowUp.isCompletedFollowUp: Boolean
    get() = status.databaseValue == "completed" || !completedAt.isNullOrBlank()

private val FollowUp.isSentMessageFollowUp: Boolean
    get() = isCompletedFollowUp && (channel == FollowUpChannel.Email || channel == FollowUpChannel.Sms)

private val FollowUp.eventDate: String
    get() = completedAt ?: dueAt

private fun String.containsAny(terms: List<String>): Boolean =
    terms.any { contains(it, ignoreCase = true) }

private fun List<Int>.averageOrNull(): Double? = if (isEmpty()) null else average()

private fun FollowUpChannel.color(): Color = when (this) {
    FollowUpChannel.Call -> Color(0xFFF59E0B)
    FollowUpChannel.Sms -> Color(0xFF22C55E)
    FollowUpChannel.Email -> Color(0xFF3B82F6)
    FollowUpChannel.Broker -> Color(0xFF8B5CF6)
    FollowUpChannel.Note -> Color(0xFF94A3B8)
}

private fun Int.rankColor(theme: com.havn.app.ui.theme.HavnThemeTokens): Color = when (this) {
    1 -> theme.primaryAccent
    2 -> theme.secondaryText
    3 -> Color(0xFFF97316)
    else -> theme.surface
}

private fun Long.analyticsPriceLabel(): String = when {
    this <= 0L -> ""
    this >= 1_000_000L -> "$${(this / 100_000L).toDouble() / 10.0}M"
    this >= 1_000L -> "$${this / 1_000L}K"
    else -> "$$this"
}

private fun List<OpenHouse>.dateRangeLabel(): String {
    val sorted = sortedBy { it.startsAt }
    val start = sorted.firstOrNull()?.startsAt?.dateLabel() ?: return "No open houses yet"
    val end = sorted.lastOrNull()?.startsAt?.dateLabel() ?: start
    return if (start == end) start else "$start - $end"
}

private fun List<String>.dateRangeLabelFromStrings(): String? {
    val sorted = filter { it.isNotBlank() }.sorted()
    val start = sorted.firstOrNull()?.dateLabel() ?: return null
    val end = sorted.lastOrNull()?.dateLabel() ?: start
    return if (start == end) start else "$start - $end"
}

private fun String?.isoDayNumberOrNull(): Int? {
    if (this.isNullOrBlank()) return null
    val parts = substringBefore("T").split("-")
    if (parts.size != 3) return null
    val year = parts[0].toIntOrNull() ?: return null
    val month = parts[1].toIntOrNull() ?: return null
    val day = parts[2].toIntOrNull() ?: return null
    if (month !in 1..12 || day !in 1..31) return null
    val monthOffset = intArrayOf(0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334)
    val leapDays = (year - 1) / 4 - (year - 1) / 100 + (year - 1) / 400
    val leap = year % 4 == 0 && (year % 100 != 0 || year % 400 == 0)
    return year * 365 + leapDays + monthOffset[month - 1] + day + if (leap && month > 2) 1 else 0
}

private fun Int.analyticsDayNumberToIso(): String {
    var low = 1900
    var high = 2500
    while (low < high) {
        val mid = (low + high + 1) / 2
        if (analyticsDayNumberFor(mid, 1, 1) <= this) {
            low = mid
        } else {
            high = mid - 1
        }
    }
    val year = low
    var month = 1
    while (month < 12 && analyticsDayNumberFor(year, month + 1, 1) <= this) {
        month += 1
    }
    val day = this - analyticsDayNumberFor(year, month, 1) + 1
    return analyticsIsoDate(year, month, day)
}

private fun analyticsDayNumberFor(year: Int, month: Int, day: Int): Int {
    val monthOffset = intArrayOf(0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334)
    val leapDays = (year - 1) / 4 - (year - 1) / 100 + (year - 1) / 400
    val leap = year % 4 == 0 && (year % 100 != 0 || year % 400 == 0)
    return year * 365 + leapDays + monthOffset[month - 1] + day + if (leap && month > 2) 1 else 0
}

private fun analyticsIsoDate(year: Int, month: Int, day: Int): String =
    "${year}-${month.analyticsTwoDigits()}-${day.analyticsTwoDigits()}"

private fun analyticsDaysInMonth(year: Int, month: Int): Int = when (month) {
    1, 3, 5, 7, 8, 10, 12 -> 31
    4, 6, 9, 11 -> 30
    2 -> if (year % 4 == 0 && (year % 100 != 0 || year % 400 == 0)) 29 else 28
    else -> 30
}

private fun Int.analyticsTwoDigits(): String = toString().padStart(2, '0')

private fun String.analyticsDateLabel(): String = substringBefore("T").dateLabel()

private fun String.analyticsSeriesLabel(language: HavnLanguage, strings: HavnStrings): String = when (this) {
    "Visitors" -> analyticsGraphLabel("visitors", language, strings)
    "Hot Leads" -> analyticsGraphLabel("hot", language, strings)
    "Tour Signals" -> analyticsGraphLabel("tours", language, strings)
    "Follow-Ups" -> analyticsGraphLabel("followUps", language, strings)
    else -> this
}

private fun analyticsGraphLabel(key: String, language: HavnLanguage, strings: HavnStrings): String = when (key) {
    "properties" -> when (language) {
        HavnLanguage.English -> strings.properties
        HavnLanguage.Slovak -> "Nehn."
        HavnLanguage.Czech -> "Nemov."
        HavnLanguage.German -> "Objekte"
        HavnLanguage.French -> "Biens"
        HavnLanguage.Spanish -> "Props."
        HavnLanguage.Italian -> "Immobili"
        HavnLanguage.Croatian -> "Nekretnine"
        HavnLanguage.Portuguese -> "Imóveis"
        HavnLanguage.Dutch -> "Woningen"
        HavnLanguage.Polish -> "Nieruch."
        HavnLanguage.Hungarian -> "Ingatlanok"
        HavnLanguage.Romanian -> "Proprietăți"
    }
    "visitors" -> when (language) {
        HavnLanguage.English -> strings.visitors
        HavnLanguage.Slovak -> "Návštevy"
        HavnLanguage.Czech -> "Návštěvy"
        HavnLanguage.German -> "Besuche"
        HavnLanguage.French -> "Visites"
        HavnLanguage.Spanish -> "Visitas"
        HavnLanguage.Italian -> "Visite"
        HavnLanguage.Croatian -> "Posjetitelji"
        HavnLanguage.Portuguese -> "Visitantes"
        HavnLanguage.Dutch -> "Bezoekers"
        HavnLanguage.Polish -> "Goście"
        HavnLanguage.Hungarian -> "Látogatók"
        HavnLanguage.Romanian -> "Vizitatori"
    }
    "interested" -> when (language) {
        HavnLanguage.English -> "Interested"
        HavnLanguage.Slovak -> "Záujem"
        HavnLanguage.Czech -> "Zájem"
        HavnLanguage.German -> "Interesse"
        HavnLanguage.French -> "Intérêt"
        HavnLanguage.Spanish -> "Interés"
        HavnLanguage.Italian -> "Interesse"
        HavnLanguage.Croatian -> "Zainteresirani"
        HavnLanguage.Portuguese -> "Interessados"
        HavnLanguage.Dutch -> "Interesse"
        HavnLanguage.Polish -> "Zainteres."
        HavnLanguage.Hungarian -> "Érdeklődők"
        HavnLanguage.Romanian -> "Interesați"
    }
    "hot" -> when (language) {
        HavnLanguage.English -> "Hot"
        HavnLanguage.Slovak -> "Horúce"
        HavnLanguage.Czech -> "Horké"
        HavnLanguage.German -> "Heiß"
        HavnLanguage.French -> "Chauds"
        HavnLanguage.Spanish -> "Cal."
        HavnLanguage.Italian -> "Caldi"
        HavnLanguage.Croatian -> "Vrući"
        HavnLanguage.Portuguese -> "Quentes"
        HavnLanguage.Dutch -> "Heet"
        HavnLanguage.Polish -> "Gorące"
        HavnLanguage.Hungarian -> "Forró"
        HavnLanguage.Romanian -> "Fierbinți"
    }
    "tours" -> when (language) {
        HavnLanguage.English -> "Tours"
        HavnLanguage.Slovak -> "Obhliadky"
        HavnLanguage.Czech -> "Prohlídky"
        HavnLanguage.German -> "Touren"
        HavnLanguage.French -> "Visites"
        HavnLanguage.Spanish -> "Visitas"
        HavnLanguage.Italian -> "Visite"
        HavnLanguage.Croatian -> "Obilasci"
        HavnLanguage.Portuguese -> "Visitas"
        HavnLanguage.Dutch -> "Rondleidingen"
        HavnLanguage.Polish -> "Prezentacje"
        HavnLanguage.Hungarian -> "Bejárások"
        HavnLanguage.Romanian -> "Vizionări"
    }
    "offers" -> when (language) {
        HavnLanguage.English -> "Offers"
        HavnLanguage.Slovak -> "Ponuky"
        HavnLanguage.Czech -> "Nabídky"
        HavnLanguage.German -> "Angebote"
        HavnLanguage.French -> "Offres"
        HavnLanguage.Spanish -> "Ofertas"
        HavnLanguage.Italian -> "Offerte"
        HavnLanguage.Croatian -> "Ponude"
        HavnLanguage.Portuguese -> "Ofertas"
        HavnLanguage.Dutch -> "Biedingen"
        HavnLanguage.Polish -> "Oferty"
        HavnLanguage.Hungarian -> "Ajánlatok"
        HavnLanguage.Romanian -> "Oferte"
    }
    "followUps" -> when (language) {
        HavnLanguage.English -> "Follow-up"
        HavnLanguage.Slovak -> "Prip."
        HavnLanguage.Czech -> "Přip."
        HavnLanguage.German -> "Follow-up"
        HavnLanguage.French -> "Relances"
        HavnLanguage.Spanish -> "Seguim."
        HavnLanguage.Italian -> "Follow-up"
        HavnLanguage.Croatian -> "Praćenja"
        HavnLanguage.Portuguese -> "Seguimentos"
        HavnLanguage.Dutch -> "Opvolging"
        HavnLanguage.Polish -> "Kontakty"
        HavnLanguage.Hungarian -> "Utánkövetés"
        HavnLanguage.Romanian -> "Urmăriri"
    }
    else -> key
}

private fun String.dateLabel(): String {
    val date = substringBefore("T")
    val parts = date.split("-")
    if (parts.size != 3) return date
    val month = when (parts[1]) {
        "01" -> "Jan"
        "02" -> "Feb"
        "03" -> "Mar"
        "04" -> "Apr"
        "05" -> "May"
        "06" -> "Jun"
        "07" -> "Jul"
        "08" -> "Aug"
        "09" -> "Sep"
        "10" -> "Oct"
        "11" -> "Nov"
        else -> "Dec"
    }
    return "$month ${parts[2].trimStart('0').ifBlank { "1" }}, ${parts[0]}"
}

private fun Double.oneDecimal(): String {
    val rounded = kotlin.math.round(this * 10.0) / 10.0
    return if (rounded % 1.0 == 0.0) rounded.toInt().toString() else rounded.toString()
}
